import { create } from 'zustand';
import type { BoardState, DrawnOpeningInfo, LineStep, Move, Premove, Square, Piece, TimeControlPreset, TimeControlKey, ClockState, ChallengeColor, MatchLegs, OpeningMode } from '@draughts/shared';
// `GameMode` in @draughts/shared è la MODALITÀ DI GIOCO (normal/breakthrough/antidraughts);
// questo store esporta un `GameMode` omonimo che è invece il tipo di partita (pvp/ai-*).
// L'alias tiene i due separati e leggibili.
import type { GameMode as RuleMode } from '@draughts/shared';
import {
  Color, GameStatus, GameVariant, PieceType, TIME_CONTROL_PRESETS, createClockState,
  DEFAULT_GAME_MODE, DEFAULT_OPENING_MODE, isDrawnOpening, resolveOpeningMode,
  CAPTURE_STEP_MS, drawnOpeningInfo,
} from '@draughts/shared';
import { createInitialBoard, getAllLegalMoves, getPremoveMoves, determineGameOutcome, findBestMoveWeakened, AI_STRENGTH_PROFILES, applyMove, serializePosition, shouldAcceptDraw, updateFrisianState, drawAndApplyOpening, replayLine, promotionHopInPassing } from '@draughts/core';
import type { AIStrengthLevel } from '@draughts/core';
import type { GameEndReason } from '@draughts/core';
import { AI_DRAW_DELAY_MS, AI_DRAW_DECLINE_NOTICE_MS, MOVABLE_HINT_MS } from '../constants/timing';
import { trackReviewModeUsed } from '../utils/analytics';
import { log } from '../utils/logger';
import { playMoveSound, playCaptureHop } from '../utils/audio';
import { vibrate } from '../utils/vibration';
import type { AIWorkerRequest, AIWorkerResponse } from '../workers/ai.worker';
import { useBlindStore } from './blind-store';

// Module-level AI Web Worker (not in store — Worker is not serializable)
let aiWorker: Worker | null = null;

// Monotonic id for each AI computation request. The worker is a reused singleton
// whose onmessage/onerror handlers are reassigned per request, so a stale result
// (from a position that no longer exists after a takeback / rematch / variant
// change) could otherwise be delivered to the current handler and applied to the
// wrong board. makeAIMove captures the current value; applyAIResult ignores any
// response whose id no longer matches. Reset points bump it to invalidate work
// that is still in flight.
let aiGeneration = 0;

// Richiamo transiente dei pezzi muovibili (vedi `movableHint` nello stato). Il contatore
// è monotono e NON è un timestamp: due tocchi nello stesso millisecondo devono comunque
// produrre due nonce diversi, o l'animazione non riparte. Fuori dallo store perché il
// timer non è stato serializzabile, come `aiWorker`.
let movableHintSeq = 0;
let movableHintTimer: ReturnType<typeof setTimeout> | null = null;

// Timer del replay della presa altrui (vedi `captureReplay` nello stato). Fuori dallo
// store come gli altri: un timer non e' stato serializzabile.
let captureReplayTimer: ReturnType<typeof setTimeout> | null = null;

function stopCaptureReplayTimer(): void {
  if (captureReplayTimer !== null) {
    clearTimeout(captureReplayTimer);
    captureReplayTimer = null;
  }
}

/**
 * Spegne il replay in corso, se c'e'.
 *
 * E' sempre sicuro: la damiera VERA e' gia' stata scritta quando il replay e' partito —
 * il replay disegna soltanto. Annullarlo non perde niente, scopre la posizione finale.
 */
function cancelCaptureReplayNow(): void {
  stopCaptureReplayTimer();
  if (useGameStore.getState().captureReplay !== null) {
    useGameStore.setState({ captureReplay: null });
  }
}

/**
 * Fa avanzare il fantasma di un salto ogni CAPTURE_STEP_MS, suonando ciascun salto.
 *
 * La cadenza combacia con quella della presa giocata a mano PER COSTRUZIONE, ed e' bene
 * sapere perche': li' `makeMove` mostra subito `path[0]` e poi `autoPlaySteps` esegue i
 * restanti `path.length - 1` salti, uno ogni CAPTURE_STEP_MS, e l'ULTIMO non e' un
 * fotogramma del fantasma ma la damiera vera. Qui identico: si parte da `step: 1`
 * (fantasma su `path[0]`) e all'ultimo tick si spegne il replay invece di disegnare
 * l'arrivo — sotto c'e' gia' la posizione finale. Totale: (path.length - 1) x CAPTURE_STEP_MS.
 *
 * Il tick che SPEGNE il replay non e' un tick a vuoto: e' il salto finale, quello che
 * scopre l'arrivo. Il suono della damatura va sul salto in cui la DAMA COMPARE, che e'
 * quello finale su tutte le varianti tranne una: con la promozione al volo (la Russa) la
 * dama nasce a meta' sequenza, e il `promote` va la' — una volta sola, non anche
 * all'arrivo (vedi `saltoDellaCorona`).
 */
function scheduleCaptureReplayTick(suono: boolean): void {
  captureReplayTimer = setTimeout(() => {
    captureReplayTimer = null;
    const replay = useGameStore.getState().captureReplay;
    if (!replay) return;
    const path = replay.move.path;
    const next = replay.step + 1;
    const corona = suono ? saltoDellaCorona(replay) : null;
    if (!path || next >= path.length) {
      // Arrivato: si scopre la damiera viva, che e' gia' la posizione finale.
      if (suono) playCaptureHop(corona !== null ? corona === next - 1 : replay.move.isPromotion);
      useGameStore.setState({ captureReplay: null });
      return;
    }
    // Il fantasma di questo tick sta su `path[next - 1]`: la damatura suona solo se la
    // dama compare proprio qui.
    if (suono) playCaptureHop(corona === next - 1);
    useGameStore.setState({ captureReplay: { ...replay, step: next } });
    scheduleCaptureReplayTick(suono);
  }, CAPTURE_STEP_MS);
}

/**
 * Il salto in cui compare la DAMA, nella presa che il fantasma sta percorrendo. `null`
 * se la mossa non promuove, o se promuove all'arrivo come su ogni variante che non ha la
 * promozione al volo (vedi `promotionHopInPassing`).
 *
 * Il pezzo si cerca nel FOTOGRAMMA e non nella damiera viva: la' e' gia' arrivato e gia'
 * promosso, e leggerlo da li' direbbe «dama» su tutti i salti.
 */
function saltoDellaCorona(replay: CaptureReplay): number | null {
  const pezzo = replay.pieces.find((p) => p.square === replay.move.from);
  if (!pezzo) return null;
  return promotionHopInPassing(replay.move, pezzo, useGameStore.getState().variant);
}

/**
 * Accende il replay: suona il PRIMO salto e mette in moto gli altri.
 *
 * Esiste per tenere insieme le due cose. Chi apre un replay ha gia' scritto
 * `captureReplay: { step: 1 }` nello stato — cioe' il primo salto e' gia' a schermo — e
 * quel fotogramma va sonorizzato subito, non al primo tick: il timer disegna i salti
 * SUCCESSIVI. Con le due chiamate separate, i due chiamanti (AI e sync esterna) potevano
 * divergere su chi suona il salto 1, ed e' esattamente il tipo di conto che si sbaglia.
 */
function avviaReplayPresa(suono: boolean): void {
  if (suono) {
    // Il fantasma e' su `path[0]`: la damatura suona subito se la dama nasce la' — e'
    // il caso di una pedina a un salto dal fondo che poi prosegue da dama volante.
    const replay = useGameStore.getState().captureReplay;
    playCaptureHop(!!replay && saltoDellaCorona(replay) === 0);
  }
  scheduleCaptureReplayTick(suono);
}

/**
 * La mossa appena arrivata dall'esterno e' una presa multipla ALTRUI da rigiocare?
 *
 * Pura ed esportata perche' e' tutta condizioni al contorno, ed e' li' che si sbaglia:
 * un resync che salta piu' mosse, una rivincita che riparte con un'altra storia, l'eco
 * della propria mossa che il giocatore ha gia' percorso a mano.
 */
export function detectExternalCapture(
  prev: Pick<BoardState, 'pieces' | 'moveHistory' | 'variant'>,
  next: BoardState,
): Move | null {
  const prevHistory = prev.moveHistory ?? [];
  const nextHistory = next.moveHistory ?? [];
  // Esattamente UNA mossa in piu': esclude i resync multi-mossa, lo snap-back del puzzle
  // e le ricostruzioni della revisione, che riscrivono la storia invece di allungarla.
  if (nextHistory.length !== prevHistory.length + 1) return null;
  if (prev.variant !== next.variant) return null;
  // ...e la storia di prima dev'essere un PREFISSO di quella nuova: una rivincita puo'
  // benissimo avere la lunghezza giusta e mosse tutt'altre.
  const prevLast = prevHistory[prevHistory.length - 1];
  if (prevLast) {
    const sameSpot = nextHistory[prevHistory.length - 1];
    if (!sameSpot || sameSpot.from !== prevLast.from || sameSpot.to !== prevLast.to) return null;
  }
  const move = nextHistory[nextHistory.length - 1];
  if (!move?.path || move.path.length <= 1) return null;
  // Il disegno affetta `captures` per il passo raggiunto: se i due array non sono
  // paralleli non c'e' una sequenza da mostrare, e meglio non inventarla.
  if (move.captures.length < move.path.length) return null;
  // Un pezzo ancora fermo sulla casella di partenza vuol dire che questa mossa NON e'
  // l'eco della nostra: quella, `executeStep` l'ha gia' percorsa salto per salto.
  if (!prev.pieces.some((p) => p.square === move.from)) return null;
  return move;
}

/**
 * True if `move` is still legal in `board` (matched on from/to and the exact set
 * of captured squares, order-independent). Used to reject a stale AI result that
 * was computed for a different position before applying it to the live board.
 */
export function isAIMoveStillLegal(board: BoardState, move: Move): boolean {
  return getAllLegalMoves(board).some(
    (m) =>
      m.from === move.from &&
      m.to === move.to &&
      m.captures.length === move.captures.length &&
      m.captures.every((c) => move.captures.some((mc) => mc.square === c.square)),
  );
}

/**
 * Build a full BoardState from the store's fields, INCLUDING the Frisian rule state
 * (kingMoveStreak + frisianEndgamePly). Every board rebuilt for legal-move generation,
 * status detection, or the AI must carry these — otherwise the Frisian/Frysk blocked-king
 * rule and the 2-kings-vs-1 draw silently stop working. Variant-gated in core, so this is
 * a no-op (fields stay undefined) for every non-Frisian variant.
 */
function boardFromStore(s: BoardState): BoardState {
  return {
    pieces: s.pieces,
    currentTurn: s.currentTurn,
    status: s.status,
    variant: s.variant,
    // Modalità di gioco (breakthrough / antidraughts): senza, l'AI cercherebbe di
    // vincere anche in antidama e non vedrebbe la promozione come fine partita.
    ruleMode: s.ruleMode,
    moveHistory: s.moveHistory,
    halfMoveClock: s.halfMoveClock,
    positionHistory: s.positionHistory ?? [],
    kingMoveStreak: s.kingMoveStreak,
    frisianEndgamePly: s.frisianEndgamePly,
  };
}

/**
 * Sorteggia e applica l'apertura del criterio scelto, restituendo anche il banner già
 * nella forma che vuole DrawnOpeningBanner. Le semi-mosse finiscono in `moveHistory`
 * come mosse vere (esattamente come lato server), così la lista mosse e la review le
 * mostrano; `openingPlyCount` è ciò che impedisce di ritirarle.
 */
function applyOpening(
  board: BoardState,
  opening: OpeningMode,
): { board: BoardState; plies: number; banner: DrawnOpeningInfo | null } {
  if (!isDrawnOpening(opening)) return { board, plies: 0, banner: null };
  const applied = drawAndApplyOpening(board, opening);
  return {
    board: applied.board,
    plies: applied.plies,
    banner: applied.opening ? drawnOpeningInfo(applied.opening) : null,
  };
}

/** Colore giocato dall'umano: 'random' si risolve qui, all'inizio della partita. */
function resolveHumanColor(color: ChallengeColor): Color {
  if (color === 'white') return Color.WHITE;
  if (color === 'black') return Color.BLACK;
  return Math.random() < 0.5 ? Color.WHITE : Color.BLACK;
}

/**
 * Re-derive the piece selection against a freshly-applied board (an opponent/AI move or a
 * mid-turn re-sync), so a selection isn't wiped every time the board changes — chess.com
 * keeps your piece selected across the opponent's move.
 *
 * - Selected piece gone (moved away — including our own just-played move — or captured):
 *   clear. This is what keeps our own optimistic move from leaving a stale selection.
 * - Now this piece's side to move: recompute its REAL legal moves (mirroring `selectSquare`'s
 *   forced-capture filter), so a single tap completes the move.
 * - Still the other side to move (e.g. a clock-tick re-sync during the opponent's turn):
 *   keep the pre-move destinations highlighted (mirroring `premoveSelectSquare`) - anche
 *   quando sono zero, perche' un pezzo murato si puo' pre-muovere lo stesso.
 * - A capture is mid-animation, or nothing was selected: clear.
 */
function recomputeSelection(
  board: BoardState,
  prevSelected: Square | null,
  prevCaptureInProgress: unknown,
): { selectedSquare: Square | null; legalMoves: Move[] } {
  const cleared = { selectedSquare: null, legalMoves: [] as Move[] };
  if (prevSelected === null || prevCaptureInProgress) return cleared;
  if (board.status !== GameStatus.PLAYING) return cleared; // no selection once the game is over

  const piece = board.pieces.find((p) => p.square === prevSelected);
  if (!piece) return cleared;

  if (piece.color === board.currentTurn) {
    // It's now this piece's turn: offer its real, legal moves. Filtering allLegal (rather than
    // getLegalMovesForPiece) respects forced captures AND the Frisian blocked-king rule.
    const allLegal = getAllLegalMoves(board);
    const legalMoves = allLegal.filter((m) => m.from === prevSelected);
    return legalMoves.length > 0 ? { selectedSquare: prevSelected, legalMoves } : cleared;
  }

  // Still the opponent's turn: keep the pre-move destinations highlighted - the same set
  // `premoveSelectSquare` offers, ZERO COMPRESO. Un pezzo murato resta scelto: la mossa
  // avversaria puo' liberarlo, e buttarne la selezione al primo battito d'orologio
  // vorrebbe dire togliergliela un attimo dopo averla fatta.
  // Il board passa cosi' com'e': i generatori leggono `piece.color`, non `board.currentTurn`.
  return { selectedSquare: prevSelected, legalMoves: getPremoveMoves(board, prevSelected) };
}

/**
 * Le caselle da cui parte almeno una mossa legale: le "sorgenti" del turno. In una
 * posizione con presa obbligatoria sono esattamente i pezzi che DEVONO catturare.
 */
function movableSquares(allLegal: Move[]): Square[] {
  return [...new Set(allLegal.map((m) => m.from))];
}

/**
 * Spegne il richiamo dopo MOVABLE_HINT_MS, ma solo se è ancora QUELLO che ha armato il
 * timer. Senza il controllo sul nonce il timer del primo tocco spegnerebbe il richiamo
 * acceso dal secondo, e ritoccare un pezzo immobile darebbe un lampo più corto ogni volta.
 */
function armMovableHintTimer(nonce: number): void {
  if (movableHintTimer) clearTimeout(movableHintTimer);
  movableHintTimer = setTimeout(() => {
    movableHintTimer = null;
    if (useGameStore.getState().movableHint?.nonce === nonce) {
      useGameStore.setState({ movableHint: null });
    }
  }, MOVABLE_HINT_MS);
}

function getAIWorker(): Worker | null {
  if (aiWorker) return aiWorker;
  if (typeof Worker === 'undefined') return null;
  try {
    aiWorker = new Worker(new URL('../workers/ai.worker.ts', import.meta.url), { type: 'module' });
    return aiWorker;
  } catch {
    log.info('Game', 'Web Worker not available, using sync fallback');
    return null;
  }
}

function terminateAIWorker(): void {
  if (aiWorker) {
    aiWorker.terminate();
    aiWorker = null;
  }
}

// Game modes
export type GameMode = 'pvp' | 'hotseat' | 'ai-very-easy' | 'ai-easy' | 'ai-medium' | 'ai-hard' | 'ai-expert' | 'ai-master';

/**
 * C'e' un'AI dall'altra parte?
 *
 * Serve perche' i modi SENZA AI ora sono due, e non uno. 'pvp' e' il valore neutro
 * che online, arena, torneo e spettatore scrivono qui dentro per spegnere il motore
 * sullo store condiviso; 'hotseat' e' una partita vera fra due persone a questo
 * dispositivo. Prima bastava `mode !== 'pvp'`, e ovunque sia rimasto quel confronto
 * l'hot-seat verrebbe scambiato per una partita contro il computer.
 */
export const isAIGameMode = (mode: GameMode): boolean => mode !== 'pvp' && mode !== 'hotseat';

/** Due persone vere a questo dispositivo (pass-and-play). */
export const isHotseatMode = (mode: GameMode): boolean => mode === 'hotseat';

/**
 * Fini partita decise da un'AZIONE e non dalla posizione: il motore non può dedurle
 * dalla scacchiera. Gli stessi nomi che il server usa per le partite online, così la
 * schermata di fine partita ne rende uno solo.
 */
export type LocalEndAction = { kind: 'resignation' | 'agreement' | 'timeout' | 'aborted'; side?: Color };

/**
 * Impostazioni con cui parte una partita locale, oltre a variante/difficoltà/orologio:
 * le stesse tre che la Sfida diretta offre fra umani. Tutte opzionali — senza, la
 * partita è quella di sempre (regole standard, mossa libera, umano col Bianco).
 */
export interface LocalGameOptions {
  ruleMode?: RuleMode;
  opening?: OpeningMode;
  /** 'random' viene risolto all'inizio della partita, non alla scelta. */
  color?: ChallengeColor;
  /**
   * 2 = andata e ritorno. Vale solo con un'apertura sorteggiata: il ritorno
   * esiste per rigiocare LA STESSA pescata dall'altra parte del tavolo.
   */
  legs?: MatchLegs;
}

/**
 * Modalità di gioco -> livello di forza dell'AI. La taratura (profondità + quota di
 * mosse casuali) sta in AI_STRENGTH_PROFILES nel core: qui c'è solo l'aggancio.
 * 'pvp' e 'hotseat' non hanno AI, quindi null.
 */
const AI_LEVELS: Record<GameMode, AIStrengthLevel | null> = {
  'pvp': null,
  'hotseat': null,
  'ai-very-easy': 'very-easy',
  'ai-easy': 'easy',
  'ai-medium': 'medium',
  'ai-hard': 'hard',
  'ai-expert': 'expert',
  'ai-master': 'master',
};

interface DragState {
  square: Square;
  offsetX: number;
  offsetY: number;
  /**
   * Quale puntatore ha preso il pezzo.
   *
   * Senza, con due dita sullo schermo il `pointerup` del SECONDO dito committava la mossa
   * del primo, alle coordinate del secondo — e `releasePointerCapture` su un puntatore mai
   * catturato lanciava `NotFoundError` dentro un handler React. La scacchiera risolve i
   * tocchi per COORDINATE e non per bersaglio, quindi niente le diceva da sola che quel
   * dito non era lo stesso.
   *
   * `null` per le prese che non nascono da un puntatore (i test, e chi chiama `startDrag`
   * a mano): un `null` accetta qualunque puntatore, cioè si comporta come prima.
   */
  pointerId: number | null;
}

/**
 * Tracks a multi-capture in progress (step-by-step).
 *
 * UN'INVARIANTE, e ci si appoggia chi disegna: le `possibleMoves` concordano sempre sui
 * salti GIA' PERCORSI, cioe' su `path[0..currentStep-1]`. Nascono filtrate su `path[0]`
 * (`makeMove`, `endDrag`) e si restringono a ogni passo su `path[currentStep]`
 * (`executeStep`), quindi divergono solo sulla CODA. E' per questo che la corona della
 * promozione al volo si puo' derivare da una candidata qualsiasi — `possibleMoves[0]` —
 * senza che la risposta dipenda da quale capita prima (vedi `ghost-piece.ts`).
 */
interface CaptureInProgress {
  possibleMoves: Move[];  // All moves that match the steps taken so far
  currentStep: number;    // Which step we're at (0 = not started, 1 = after first capture, etc.)
  currentSquare: Square;  // Current position of the piece
  capturedSoFar: Square[]; // Pieces captured so far (faded until the final landing)
  originalFrom: Square;   // Original starting square (for piece lookup)
}

/**
 * Il replay della presa multipla giocata da QUALCUN ALTRO: l'avversario online, il bot,
 * l'AI locale, la risposta della soluzione di un puzzle.
 *
 * E' uno stato di solo DISEGNO, e la differenza con `captureInProgress` e' tutta qui.
 * `captureInProgress` e' la presa del giocatore mentre la sta facendo: la damiera vera
 * non e' ancora avanzata, e quello stato guida anche l'input (`executeStep` lo consuma,
 * `selectedSquare` lo segue). Qui invece la damiera vera e' GIA' quella finale — orologi,
 * turno, lista mosse e fine partita sono a posto dal primo istante — e questo congela
 * l'istantanea di prima solo per farla scorrere sopra.
 */
interface CaptureReplay {
  /**
   * I pezzi PRIMA della mossa. Non e' lo stato: e' un fotogramma.
   *
   * Quindi il pezzo che viaggia sta qui ancora da PEDINA, anche quando la mossa promuove:
   * la corona, a chi disegna, la dice `ghostPiece` salto per salto.
   */
  pieces: Piece[];
  /** La mossa risolta, con `path.length > 1`. */
  move: Move;
  /** 1..path.length - 1 — il pezzo e' disegnato su `move.path[step - 1]`. */
  step: number;
}

// Result of tapping a capture-landing square with no piece selected.
type DirectTapResult =
  | { kind: 'direct'; from: Square; moves: Move[] }
  | { kind: 'choose'; to: Square; froms: Square[] };

interface GameStore extends BoardState {
  // Actions
  // `immediate` applies a multi-capture in one shot (no step-by-step animation).
  // NESSUN chiamante di produzione lo passa: era la vecchia strada della premossa, che
  // ora percorre i salti come ogni altra presa. Resta perche' due test lo usano per
  // applicare una presa intera senza seguirla passo a passo.
  makeMove: (move: Move, immediate?: boolean) => void;
  /**
   * Gioca un passo della presa. Restituisce la mossa COMPLETATA se quel passo era
   * l'ultimo, altrimenti `null`.
   *
   * Il valore di ritorno non è un comodo: è l'unico segnale affidabile di «presa
   * finita». Chi lo deduceva dalla transizione di `captureInProgress` a `null`
   * (la damiera, per decidere quando spedire la mossa online) si sbagliava
   * quando `syncFromExternal` azzerava quel campo per un aggiornamento arrivato
   * dal server a metà sequenza: la damiera concludeva «presa finita» e spediva
   * l'ULTIMA mossa dello storico, che in quel momento poteva essere quella
   * dell'avversario.
   */
  executeStep: (targetSquare: Square) => Move | null;
  resetGame: () => void;
  selectSquare: (square: Square | null) => void;
  startDrag: (square: Square, offsetX: number, offsetY: number, pointerId?: number) => void;
  updateDragPosition: (x: number, y: number) => void;
  /**
   * Chiude una presa trascinata. Come `executeStep`, restituisce la mossa
   * COMPLETATA quando il rilascio era sull'ultimo salto, altrimenti `null`: è
   * l'altra metà del segnale esplicito di fine presa.
   */
  endDrag: (targetSquare: Square | null) => Move | null;
  // In a forced-capture position, resolve a tapped capture-landing square. Returns:
  //  - { kind: 'direct', from, moves } when exactly one piece can land there (selects
  //    it as a side effect, so it can be played without picking the piece first);
  //  - { kind: 'choose', to, froms } when several different pieces can land there
  //    (sets captureSourceChoices/pendingCaptureTarget so the player picks one);
  //  - null when captures aren't forced or the square isn't a capture landing.
  findDirectMove: (square: Square) => DirectTapResult | null;
  stopDrag: () => void;
  cancelDrag: () => void;
  setGameMode: (mode: GameMode) => void;
  setVariant: (variant: GameVariant) => void;
  /** Start a local (vs-AI or pvp) game from the standard opening. `options` carries the
   *  three settings the Sfida AI modal offers; omitting it keeps the historical
   *  behaviour (regole standard, mossa libera, umano col Bianco). */
  startLocalGame: (variant: GameVariant, mode: GameMode, timeControl: TimeControlKey, options?: LocalGameOptions) => void;
  /** Start a local (vs-AI or pvp) game from a GIVEN board position, with the human
   *  playing `humanColor`. Used to continue a solved puzzle against the AI. */
  startLocalGameFromBoard: (board: BoardState, mode: GameMode, timeControl: TimeControlKey, humanColor: Color, options?: { ruleMode?: RuleMode }) => void;
  makeAIMove: () => void;
  resign: () => void;
  offerDraw: () => void; // AI mode only: player offers a draw, the AI accepts or declines
  /** Patta d'accordo, immediata. Solo in presenza: i due giocatori sono qui. */
  agreeDraw: () => void;
  /** Chiude la partita senza vincitore. Solo in presenza: vedi l'azione. */
  abandonGame: () => void;
  /** Rigioca il ritorno: stessa apertura sorteggiata dell'andata, orologi nuovi. */
  startReturnLeg: () => void;
  /**
   * Scrive una damiera decisa altrove (server, soluzione di un puzzle, spettatore).
   *
   * `replay: false` per le scritture che NON sono una mossa appena giocata — il carico
   * iniziale di una posizione, la navigazione della soluzione, lo snap-back dopo una
   * mossa sbagliata: li' non c'e' niente da rigiocare.
   */
  syncFromExternal: (state: BoardState, opts?: { replay?: boolean; suono?: boolean }) => void;
  /** Spegne il replay della presa altrui e scopre la posizione finale (gia' scritta). */
  cancelCaptureReplay: () => void;

  // Clock actions
  setTimeControl: (preset: TimeControlPreset) => void;
  updateClock: (player: Color, timeRemaining: number) => void;
  handleTimeExpired: (player: Color) => void;
  addIncrement: (player: Color) => void;

  // UI State
  selectedSquare: Square | null;
  legalMoves: Move[];
  dragState: DragState | null;
  dragPosition: { x: number; y: number } | null;
  captureInProgress: CaptureInProgress | null;
  captureReplay: CaptureReplay | null;

  // Pre-move: intent queued during the opponent's turn, re-validated and played
  // (or discarded) once it becomes our turn again. One pre-move at a time, always
  // piece -> square (a destination-only pre-move no longer exists).
  premove: Premove | null;
  premoveSelectSquare: (square: Square) => void;
  // Like premoveSelectSquare but also starts a drag, so a pre-move can be set by
  // dragging the piece (not only tap-tap).
  premoveStartDrag: (square: Square, offsetX: number, offsetY: number, pointerId?: number) => void;
  setPremove: (premove: Premove) => void;
  clearPremove: () => void;

  // Ambiguous capture: tapping an empty landing square reachable by several pieces
  // highlights those source squares so the player can pick which piece captures.
  captureSourceChoices: Square[];
  pendingCaptureTarget: Square | null;
  selectCaptureSource: (from: Square) => Move[];
  clearCaptureChoices: () => void;

  // Richiamo TRANSIENTE dopo un tocco che non produce niente (pezzo immobile per via di
  // una presa obbligatoria, pezzo avversario, casella vuota, tocco fuori percorso a metà
  // presa): le caselle da cui parte almeno una mossa legale si illuminano per un attimo.
  // `nonce` cambia a ogni richiamo anche a caselle invariate — è ciò che fa RIPARTIRE
  // l'animazione se si ritocca lo stesso pezzo immobile entro la finestra.
  movableHint: { squares: Square[]; nonce: number } | null;
  /** Accende il richiamo: senza argomento sulle sorgenti del turno, con un argomento su
   *  caselle precise (la presa multipla da finire). */
  flashMovableHint: (squares?: Square[]) => void;
  clearMovableHint: () => void;

  // Game mode
  gameMode: GameMode;
  isAIThinking: boolean;
  localPlayerColor: Color; // Which color the human plays in local/AI games

  // Draw offer (AI mode): player has offered a draw and the AI is deciding;
  // aiDeclinedDraw is a transient notice shown briefly after a decline.
  drawOffered: boolean;
  aiDeclinedDraw: boolean;
  drawTimeoutId: ReturnType<typeof setTimeout> | null;

  // Board the current game began from — the origin moveHistory is replayed from.
  // null means "the standard opening for `variant`" (so a variant switch needs no
  // update); startLocalGameFromBoard sets a real board for puzzle continuations.
  //
  // Due scrittori, non uno. Il secondo e' la partita ONLINE da posizione composta
  // (sfida dall'editor): la board la manda il SERVER su ogni porta che consegna una
  // partita, e la scrive `setOnlineStartBoard` — vedi la sua nota per il perche' non
  // la si possa leggere da `multiplayer-store` da qui dentro.
  startBoard: BoardState | null;

  // Modalità di gioco (normal / breakthrough / antidraughts). BoardState la dichiara
  // opzionale; qui è sempre valorizzata, così ogni board ricostruita la porta con sé.
  ruleMode: RuleMode;

  // Criterio di apertura GIÀ RISOLTO contro la variante ('ballot*' diventa 'random3'
  // dove non esiste tabella federale). La rivincita ripesca da qui.
  openingMode: OpeningMode;
  // Quante semi-mosse iniziali sono state imposte dall'apertura: sono in moveHistory
  // come mosse vere, ma non si possono ritirare.
  openingPlyCount: number;
  // L'apertura sorteggiata, per il banner a inizio partita (null = mossa libera).
  drawnOpening: DrawnOpeningInfo | null;
  /**
   * Match locale in due partite (andata e ritorno), o 1 per la partita secca.
   *
   * Senza account non c'e' nessuno da nominare e nessun punteggio da tenere: i due
   * giocatori si scambiano di posto da soli. Quello che serve qui e' solo sapere
   * se il ritorno va offerto, e da quale posizione ripartire.
   */
  matchLegs: MatchLegs;
  matchLegIndex: 1 | 2;
  /** La posizione post-apertura dell'andata, da rigiocare identica al ritorno. */
  legOpeningBoard: BoardState | null;

  /**
   * Perché la partita è finita (null finché è in corso). Due sorgenti:
   * il motore, per le fini determinate dalla posizione (`GameEndReason`), e le azioni
   * dei giocatori, che la posizione non racconta — abbandono, patta accordata, tempo.
   * Senza questo la schermata di fine partita tirava a indovinare, e diceva "il Nero non
   * ha più mosse" anche dopo un abbandono.
   */
  endReason: GameEndReason | LocalEndAction | null;

  // Review mode (view past board states without modifying game)
  reviewIndex: number | null; // null = live, 0..moveHistory.length = viewing that state
  reviewPieces: Piece[] | null; // computed board at reviewIndex
  reviewTurn: Color | null; // side to move in the reviewed position (from reconstruction)
  /**
   * La striscia frisone della posizione RIVISTA, ricostruita insieme a `reviewPieces`.
   *
   * Serve ai pallini della dama bloccata: `reviewToIndex` rigioca la storia con
   * `applyMove`, quindi la striscia di quel ply esiste gia' — veniva solo buttata via. Con
   * la striscia VIVA i pallini cadrebbero sulla casella di un'altra posizione. Resta
   * `null` fuori dalla revisione e sulle varianti senza la regola, dove `applyMove` non
   * scrive il campo affatto.
   */
  reviewKingMoveStreak: BoardState['kingMoveStreak'] | null;
  /**
   * Ingresso in una partita ONLINE (lobby, sfida, arena, torneo).
   *
   * I tre punti d'ingresso facevano `useGameStore.setState({ gameMode: 'pvp' })`
   * a mano, che scrive UNA chiave e lascia tutto il resto della sessione
   * precedente in piedi — in particolare `startBoard`, la posizione da cui una
   * partita LOCALE composta era iniziata (editor → «Analisi», continuazione di
   * un puzzle). `reviewToIndex` ricostruisce da `startBoard` e rigioca
   * `moveHistory` sopra: con una posizione di partenza estranea `applyMove`
   * LANCIA, e l'eccezione — dentro l'updater di zustand, chiamato da un onClick
   * — risale fino all'ErrorBoundary di `main.tsx`, che sostituisce L'APP INTERA
   * con «Something went wrong». Bastava cliccare una mossa nella lista, con la
   * partita a tempo ancora in corso sul server.
   *
   * L'argomento e' la posizione di partenza di questa partita ONLINE, quando non
   * e' l'apertura standard: la manda il server (`campoPosizioneIniziale`) ed e'
   * l'unico modo in cui il client la conosce. Il valore di riposo e' `null` —
   * cioe' l'azzeramento di prima — perche' chi entra senza passarla (arena,
   * torneo) non ha mai una posizione composta, e perche' e' proprio
   * l'azzeramento la difesa descritta qui sopra.
   */
  enterOnlineGame: (startBoard?: BoardState | null) => void;
  /**
   * Scrive la posizione di partenza di una partita ONLINE, da fuori.
   *
   * Serve perche' la board arriva dal server DOPO (rivincita, ritorno di manche,
   * rientro: tutte cose che capitano a schermata gia' montata, dove
   * `enterOnlineGame` non rigira) e perche' da qui dentro non si puo' leggere
   * `multiplayer-store`: e' lui a importare questo store, e due `create()` in
   * ciclo si vedrebbero `undefined` a seconda di chi entra prima nel grafo. Il
   * valore scende quindi dall'esterno, dal solo posto che lo riceve.
   *
   * `null` significa «apertura standard» e va passato: il campo NON e'
   * appiccicoso. Lasciare in piedi la posizione della partita prima e' l'unica
   * strada per cui `applyMove` ricominci a lanciare dentro un updater di
   * zustand, cioe' l'incidente che `enterOnlineGame` ripara.
   */
  setOnlineStartBoard: (board: BoardState | null) => void;
  reviewToIndex: (index: number | null) => void; // navigate to any ply (view-only, both modes)
  reviewBack: () => void;
  reviewForward: () => void;
  reviewExit: () => void;
  /** Drop history after reviewIndex and resume play from that position. AI only —
   *  `force` is the one exception, for the editor's analysis board, which is a hot-seat
   *  scratch line where truncating IS the interaction. Never force it in a real game. */
  commitTakeback: (force?: boolean) => void;
  /** Rigioca una linea salvata sulla partita locale appena seminata. Vedi l'azione. */
  replayLocalLine: (steps: readonly LineStep[]) => void;

  // Clock state
  clockState: ClockState;
  /**
   * La cadenza in corso. E' una TimeControlKey, non un TimeControlPreset: la partita
   * personalizzata lascia comporre minuti x incremento liberamente (~490 combinazioni),
   * e `resolveTimeControl` sa gia' leggerle tutte. I preset restano un sottoinsieme
   * valido, quindi nessun chiamante esistente cambia.
   */
  timeControlPreset: TimeControlKey;

  // AI timeout tracking (FIX-92)
  aiTimeoutId: ReturnType<typeof setTimeout> | null;

  // Wall-clock ms when the current local game began (set in startLocalGame*);
  // used to compute real game duration for analytics. 0 before any game starts.
  startedAtMs: number;
}

const initialBoard = createInitialBoard(GameVariant.ITALIAN);

export const useGameStore = create<GameStore>((set, get) => ({
  // Initial board state from core
  pieces: initialBoard.pieces,
  currentTurn: Color.WHITE,
  status: GameStatus.PLAYING,
  variant: GameVariant.ITALIAN,
  moveHistory: [],
  halfMoveClock: 0,
  positionHistory: [],
  kingMoveStreak: undefined,
  frisianEndgamePly: null,
  startBoard: null,
  ruleMode: DEFAULT_GAME_MODE,
  openingMode: DEFAULT_OPENING_MODE,
  openingPlyCount: 0,
  drawnOpening: null,
  matchLegs: 1 as MatchLegs,
  matchLegIndex: 1 as 1 | 2,
  legOpeningBoard: null as BoardState | null,
  endReason: null,

  // UI state
  selectedSquare: null,
  legalMoves: [],
  dragState: null,
  dragPosition: null,
  captureInProgress: null,
  captureReplay: null,
  premove: null,
  captureSourceChoices: [],
  pendingCaptureTarget: null,
  movableHint: null,

  // Game mode
  gameMode: 'pvp' as GameMode,
  isAIThinking: false,
  localPlayerColor: Color.WHITE,

  // Draw offer (AI mode)
  drawOffered: false,
  aiDeclinedDraw: false,
  drawTimeoutId: null,
  startedAtMs: 0,

  // Review mode
  reviewIndex: null,
  reviewPieces: null,
  reviewKingMoveStreak: null,
  reviewTurn: null,

  // Clock state
  clockState: createClockState('none'),
  timeControlPreset: 'none' as TimeControlKey,

  // AI timeout tracking
  aiTimeoutId: null,

  // Actions
  makeMove: (move: Move, immediate = false) => {
    log.info('Game', 'makeMove() called', { from: move.from, to: move.to });
    // `immediate` plays a multi-capture in one shot, so it is not treated as a
    // step-by-step sequence and DOES get the move sound/vibration. Solo per i test:
    // vedi la nota sulla dichiarazione.
    const isMultiCapture = !immediate && !!(move.path && move.path.length > 1);
    let applied = true;
    /* La dama nasce GIA' al primo salto? Succede solo con la promozione al volo. */
    let coronaAlPrimoSalto = false;
    set((state) => {
      // Guard: if no piece sits on the from-square, this move was already applied
      // (e.g. a multi-capture finalized locally then re-sent for server
      // confirmation in puzzle/online mode). Re-processing it would orphan a
      // captureInProgress and flash the captured pieces. No-op.
      if (!state.pieces.some((p) => p.square === move.from)) {
        log.warn('Game', 'makeMove no-op: no piece on from-square (already applied?)', { from: move.from, to: move.to });
        applied = false;
        return {};
      }

      // If this is a multi-capture move (has path with more than 1 step), start
      // step-by-step — unless `immediate`, which applies the whole move at once.
      if (!immediate && move.path && move.path.length > 1) {
        log.info('Game', 'makeMove multi-capture start (step-by-step)', { from: move.from, to: move.to, steps: move.path.length });
        const firstStep = move.path[0]!;
        const firstCapture = move.captures[0]!.square;

        // Find ALL moves that start with the same first step (for landing choice)
        const matchingMoves = state.legalMoves.filter((m) =>
          m.path && m.path.length > 1 && m.path[0] === firstStep
        );

        const pezzoInViaggio = state.pieces.find((p) => p.square === move.from);
        coronaAlPrimoSalto = !!pezzoInViaggio
          && promotionHopInPassing(move, pezzoInViaggio, state.variant) === 0;

        return {
          captureInProgress: {
            possibleMoves: matchingMoves.length > 0 ? matchingMoves : [move],
            currentStep: 1,
            currentSquare: firstStep,
            capturedSoFar: [firstCapture],
            originalFrom: move.from,
          },
          selectedSquare: firstStep,
          legalMoves: [] as Move[],
        };
      }

      // Simple move or single capture - execute immediately
      const movingPiece = state.pieces.find((p) => p.square === move.from);
      const newPieces = state.pieces
        .filter((p) => !move.captures.some((c) => c.square === p.square))
        .map((p) => {
          if (p.square === move.from) {
            return {
              color: p.color,
              type: move.isPromotion ? PieceType.KING : p.type,
              square: move.to,
            };
          }
          return p;
        });

      const newTurn = state.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
      const newMoveHistory = [...state.moveHistory, move];
      // FIX-88: Reset for captures OR man moves (matches core engine)
      const newHalfMoveClock = (move.captures.length > 0 || (movingPiece && movingPiece.type === PieceType.MAN)) ? 0 : state.halfMoveClock + 1;

      // FIX-80: Build board state with positionHistory for threefold detection
      const newBoardState: BoardState = {
        pieces: newPieces,
        currentTurn: newTurn,
        status: GameStatus.PLAYING,
        variant: state.variant,
        ruleMode: state.ruleMode,
        moveHistory: newMoveHistory,
        halfMoveClock: newHalfMoveClock,
        positionHistory: state.positionHistory ?? [],
        kingMoveStreak: state.kingMoveStreak,
        frisianEndgamePly: state.frisianEndgamePly,
      };
      const positionHash = serializePosition(newBoardState);
      const newPositionHistory = [...(state.positionHistory ?? []), positionHash];
      newBoardState.positionHistory = newPositionHistory;
      // Maintain Frisian state (no-op for non-Frisian) BEFORE reading the game status.
      updateFrisianState(boardFromStore(state), newBoardState, move, movingPiece?.type ?? PieceType.MAN);
      const { status: newStatus, reason: newEndReason } = determineGameOutcome(newBoardState);
      log.info('Game', 'makeMove applied (simple/single-capture)', { from: move.from, to: move.to, newTurn, newStatus });

      return {
        pieces: newPieces,
        currentTurn: newTurn,
        status: newStatus,
        endReason: newEndReason,
        moveHistory: newMoveHistory,
        halfMoveClock: newHalfMoveClock,
        positionHistory: newPositionHistory,
        kingMoveStreak: newBoardState.kingMoveStreak,
        frisianEndgamePly: newBoardState.frisianEndgamePly,
        selectedSquare: null,
        legalMoves: [],
        captureInProgress: null,
        premove: null,
        captureSourceChoices: [],
        pendingCaptureTarget: null,
        movableHint: null,
        reviewIndex: null,
        reviewPieces: null,
        reviewKingMoveStreak: null,
        reviewTurn: null,
        // A move supersedes any pending draw offer / decline notice.
        drawOffered: false,
        aiDeclinedDraw: false,
      };
    });
    if (!applied) return;
    if (isMultiCapture) {
      // La sequenza e' appena partita e `path[0]` e' gia' a schermo: questo e' il salto 1,
      // e ha il suo suono come tutti gli altri. La vibrazione no — quella resta UNA per
      // mossa, sul salto che conclude (vedi `executeStep`): nove vibrazioni di fila
      // sarebbero un allarme, non un riscontro. La damatura invece puo' toccare proprio
      // questo salto: con la promozione al volo la dama nasce dove la pedina atterra.
      log.info('Game', 'makeMove → suono del primo salto', { from: move.from, to: move.to });
      playCaptureHop(coronaAlPrimoSalto);
      return;
    }
    log.info('Game', 'makeMove → move sound', { from: move.from, to: move.to });
    playMoveSound(move.isPromotion, undefined, move.captures.length > 0);
    vibrate(40);
  },

  executeStep: (targetSquare: Square) => {
    let finalized = false;
    /* Il salto appena disegnato e' quello in cui compare la dama? */
    let suonaCorona = false;
    /* Il passo e' stato fatto davvero? I due rami di no-op qui sotto non devono suonare. */
    let avanzato = false;
    /* La mossa completata, per chi ha bisogno di sapere QUALE (vedi il tipo). */
    let mossaCompletata: Move | null = null;
    set((state) => {
      if (!state.captureInProgress) return {};

      const { possibleMoves, currentStep, capturedSoFar, originalFrom } = state.captureInProgress;

      // Filter moves that have targetSquare at the current step
      const matchingMoves = possibleMoves.filter((m) => {
        const path = m.path;
        if (!path || currentStep >= path.length) return false;
        return path[currentStep] === targetSquare;
      });

      if (matchingMoves.length === 0) {
        log.warn('Game', 'executeStep no-op: no move matches target at current step', { targetSquare, currentStep });
        return {};
      }

      // Get the capture for this step (should be same for all matching moves at this step)
      const firstMove = matchingMoves[0]!;
      const nextCapture = firstMove.captures[currentStep]?.square;
      if (nextCapture === undefined) {
        log.warn('Game', 'executeStep no-op: missing capture for current step', { targetSquare, currentStep });
        return {};
      }
      const newCapturedSoFar = [...capturedSoFar, nextCapture];

      // Check if this is the last step for all remaining moves
      const allAtLastStep = matchingMoves.every((m) =>
        m.path && currentStep === m.path.length - 1
      );

      avanzato = true;

      const pezzoInViaggio = state.pieces.find((p) => p.square === originalFrom);
      const corona = pezzoInViaggio
        ? promotionHopInPassing(firstMove, pezzoInViaggio, state.variant)
        : null;
      /*
       * Il salto appena disegnato ha indice `currentStep` (le candidate sono filtrate su
       * `path[currentStep] === targetSquare`). Dove la promozione e' al volo la damatura
       * suona la', e NON si ripete all'arrivo: `firstMove.isPromotion` resta vero per
       * tutta la mossa, quindi questo deve essere un ternario e non un `||`.
       */
      suonaCorona = corona !== null ? corona === currentStep : allAtLastStep && firstMove.isPromotion;

      if (allAtLastStep) {
        finalized = true;
        log.info('Game', 'executeStep finalize (capture sequence complete)', { targetSquare, from: firstMove.from });
        // Complete the capture - use the first matching move
        const newPieces = state.pieces
          .filter((p) => !firstMove.captures.some((c) => c.square === p.square))
          .map((p) => {
            if (p.square === originalFrom) {
              return {
                color: p.color,
                type: firstMove.isPromotion ? PieceType.KING : p.type,
                square: targetSquare,
              };
            }
            return p;
          });

        // Create completed move with actual final destination
        const completedMove: Move = {
          from: firstMove.from,
          to: targetSquare,
          captures: firstMove.captures,
          isPromotion: firstMove.isPromotion,
          path: firstMove.path,
        };
        mossaCompletata = completedMove;

        const newTurn = state.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
        const newMoveHistory = [...state.moveHistory, completedMove];

        // FIX-80: Build board state with positionHistory for threefold detection
        const newBoardState: BoardState = {
          pieces: newPieces,
          currentTurn: newTurn,
          status: GameStatus.PLAYING,
          variant: state.variant,
          ruleMode: state.ruleMode,
          moveHistory: newMoveHistory,
          halfMoveClock: 0,
          positionHistory: state.positionHistory ?? [],
          kingMoveStreak: state.kingMoveStreak,
          frisianEndgamePly: state.frisianEndgamePly,
        };
        const positionHash = serializePosition(newBoardState);
        const newPositionHistory = [...(state.positionHistory ?? []), positionHash];
        newBoardState.positionHistory = newPositionHistory;
        const movingType = pezzoInViaggio?.type ?? PieceType.MAN;
        updateFrisianState(boardFromStore(state), newBoardState, completedMove, movingType);
        const { status: newStatus, reason: newEndReason } = determineGameOutcome(newBoardState);

        return {
          pieces: newPieces,
          currentTurn: newTurn,
          status: newStatus,
          endReason: newEndReason,
          moveHistory: newMoveHistory,
          halfMoveClock: 0,
          positionHistory: newPositionHistory,
          kingMoveStreak: newBoardState.kingMoveStreak,
          frisianEndgamePly: newBoardState.frisianEndgamePly,
          selectedSquare: null,
          legalMoves: [],
          captureInProgress: null,
          movableHint: null,
        };
      }

      // Not at last step - advance with filtered moves
      return {
        captureInProgress: {
          possibleMoves: matchingMoves,
          currentStep: currentStep + 1,
          currentSquare: targetSquare,
          capturedSoFar: newCapturedSoFar,
          originalFrom,
        },
        selectedSquare: targetSquare,
        movableHint: null,
      };
    });
    // Un suono per OGNI salto, nel momento in cui il salto viene disegnato: e' quello che
    // rende una presa multipla una sequenza invece di uno scatto. La damatura suona nel
    // salto in cui la dama COMPARE — l'ultimo, tranne con la promozione al volo, dove
    // nasce a meta' sequenza — e una volta sola. La vibrazione invece resta una per mossa,
    // su quello che conclude.
    if (avanzato) {
      log.info('Game', 'executeStep → suono del salto', { finalized, suonaCorona });
      playCaptureHop(suonaCorona);
    }
    if (finalized) vibrate(40);
    return mossaCompletata;
  },

  resetGame: () => {
    aiGeneration++; // invalidate any AI result still in flight for the old game
    cancelCaptureReplayNow(); // la partita vecchia non ha piu' niente da mostrare
    log.info('Game', 'resetGame() called', { aiGeneration });
    // Ogni partita alla cieca riparte con zero sbirciate: il conto descrive la partita,
    // non la sessione, e una rivincita è una partita nuova.
    useBlindStore.getState().resetBlindSession();
    set((state) => {
      // FIX-92: Cancel pending AI timeout
      if (state.aiTimeoutId) clearTimeout(state.aiTimeoutId);
      if (state.drawTimeoutId) clearTimeout(state.drawTimeoutId);
      terminateAIWorker();

      // La rivincita rigioca la stessa impostazione: stessa modalità, e una NUOVA
      // pescata dallo stesso criterio di apertura (come fa il rematch online).
      const applied = applyOpening(createInitialBoard(state.variant, state.ruleMode), state.openingMode);
      const newBoard = applied.board;
      // Swap color on rematch in AI mode (only if a game was actually played).
      // Le semi-mosse dell'apertura NON contano come partita giocata: sono già in
      // moveHistory prima che qualcuno muova.
      const isAI = isAIGameMode(state.gameMode);
      const wasPlayed = state.moveHistory.length > state.openingPlyCount;
      const newColor = (isAI && wasPlayed)
        ? (state.localPlayerColor === Color.WHITE ? Color.BLACK : Color.WHITE)
        : state.localPlayerColor;
      return {
        pieces: newBoard.pieces,
        currentTurn: newBoard.currentTurn,
        status: newBoard.status,
        moveHistory: newBoard.moveHistory,
        halfMoveClock: newBoard.halfMoveClock,
        positionHistory: newBoard.positionHistory ?? [],
        kingMoveStreak: newBoard.kingMoveStreak,
        frisianEndgamePly: newBoard.frisianEndgamePly ?? null,
        startBoard: null, // back to the standard opening
        openingPlyCount: applied.plies,
        drawnOpening: applied.banner,
        // Rivincita = match nuovo: si riparte dall'andata, con una nuova pescata.
        matchLegIndex: 1 as 1 | 2,
        legOpeningBoard: state.matchLegs === 2 ? newBoard : null,
        endReason: null,
        // Il banner si ri-arma sul cambio di startedAtMs: senza aggiornarlo, la
        // rivincita non mostrerebbe la nuova apertura (e la durata della partita
        // per le statistiche partirebbe da quella precedente).
        startedAtMs: Date.now(),
        selectedSquare: null,
        legalMoves: [],
        captureInProgress: null,
        // Una presa non sopravvive alla fine di una partita: se il dito se n'era andato
        // senza `pointerup`, il `pointerId` rimasto respingerebbe OGNI tocco nuovo e la
        // damiera della partita dopo nascerebbe morta. Vedi la guardia in GameBoard.
        dragState: null,
        dragPosition: null,
        premove: null,
        captureSourceChoices: [],
        pendingCaptureTarget: null,
        movableHint: null,
        reviewIndex: null,
        reviewPieces: null,
        reviewKingMoveStreak: null,
        reviewTurn: null,
        localPlayerColor: newColor,
        isAIThinking: false,
        aiTimeoutId: null,
        drawOffered: false,
        aiDeclinedDraw: false,
        drawTimeoutId: null,
        // FIX-87: Reset clock on rematch
        clockState: createClockState(state.timeControlPreset),
      };
    });
  },

  selectSquare: (square: Square | null) => {
    // Il richiamo dei pezzi muovibili si accende DOPO il set: dentro l'updater non vanno
    // effetti collaterali né set annidati. È lo schema di endDrag, che cattura l'esito
    // dentro e suona fuori.
    let rejected = false;
    set((state) => {
      if (square === null) {
        // Deselezione esplicita: non è un tocco a vuoto, nessun richiamo.
        return { selectedSquare: null, legalMoves: [] };
      }

      // Check if square has a piece of current player
      const piece = state.pieces.find((p) => p.square === square);
      if (!piece || piece.color !== state.currentTurn) {
        // Il tocco su un pezzo avversario passa da startDrag, che accende il richiamo lì;
        // qui ci arrivano solo chiamate programmatiche, che restano silenziose.
        return { selectedSquare: null, legalMoves: [] };
      }

      // Calculate legal moves using core engine (carry Frisian state for blocked-king filtering)
      const boardState = boardFromStore(state);

      // Get all legal moves to check if capture is required
      const allLegal = getAllLegalMoves(boardState);
      const hasCaptures = allLegal.some((m) => m.captures.length > 0);

      // If captures are required, only pieces that can capture should be selectable
      if (hasCaptures) {
        const pieceMoves = allLegal.filter((m) => m.from === square);
        if (pieceMoves.length === 0) {
          rejected = true;
          return { selectedSquare: null, legalMoves: [], movableHint: null };
        }
        return { selectedSquare: square, legalMoves: pieceMoves, movableHint: null };
      }

      // No captures required: derive this piece's moves from allLegal, which already applied the
      // Frisian blocked-king filter (getLegalMovesForPiece does not) — no effect for other variants.
      const legalMoves = allLegal.filter((m) => m.from === square);
      // Un pezzo senza mosse (la dama frisone bloccata) va DESELEZIONATO come fa startDrag:
      // lasciarlo selezionato con zero destinazioni è uno stato che non vuol dire niente.
      if (legalMoves.length === 0) {
        rejected = true;
        return { selectedSquare: null, legalMoves: [], movableHint: null };
      }
      return { selectedSquare: square, legalMoves, movableHint: null };
    });
    if (rejected) get().flashMovableHint();
  },

  /*
   * Pre-mossa, scelta del pezzo: per una NOSTRA pedina durante il turno avversario.
   *
   * Offre le mosse VERE del pezzo nella posizione attuale, senza il solo obbligo di presa
   * (vedi `getPremoveMoves`). Prima offriva un insieme di sola geometria, e i pallini
   * mentivano: arrivi a due caselle di diagonale anche senza nessuno da saltare, arrivi
   * sopra pezzi, e tutto disegnato come identico pallino verde perche' le mosse erano
   * finte (`captures: []`). Ora le mosse sono quelle vere, quindi una presa porta il suo
   * anello e una mossa quieta il suo pallino.
   *
   * Cio' che si ACCETTA e' piu' ampio di cio' che si SUGGERISCE, da tutte e due le parti.
   * Sull'arrivo: GameBoard accoda anche una casella senza pallino. E sulla sorgente: un
   * pezzo che adesso non ha nessuna mossa resta selezionabile, con zero destinazioni a
   * schermo. Non e' una svista — una pre-mossa non si gioca adesso, si gioca DOPO la mossa
   * avversaria, che il pezzo puo' benissimo liberarlo (il nemico che lo blocca si sposta,
   * la pedina amica che lo blocca viene mangiata). Rifiutare la selezione faceva sembrare
   * la pre-mossa rotta proprio sulle file di fondo, dove i pezzi sono murati dai propri.
   *
   * Ogni ramo azzera `premove`: chi comincia a comporre una pre-mossa nuova ha gia' buttato
   * quella in coda, e vale sia dal tocco sia dal trascinamento (`premoveStartDrag` delega
   * qui), senza dipendere da quale ramo della UI ha chiamato.
   *
   * Niente richiamo sui pezzi muovibili quando il pezzo non va bene: quello e' un istituto
   * del PROPRIO turno («queste si possono muovere ora»), e qui si muove l'avversario.
   */
  premoveSelectSquare: (square: Square) => {
    set((state) => {
      const premoveColor = state.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
      const piece = state.pieces.find((p) => p.square === square);
      /*
       * Pezzo sbagliato: casella vuota o colore avversario. Dal gesto normale non ci si
       * arriva quasi mai — `GameBoard` guarda il colore prima di chiamare — ma il ramo non
       * e' morto: nel turno avversario la damiera puo' cambiare fra il disegno e il tocco
       * (arriva la mossa dell'altro), e in quella corsa i pezzi della closure sono vecchi
       * mentre lo store no.
       */
      if (!piece || piece.color !== premoveColor) {
        return { selectedSquare: null, legalMoves: [], premove: null };
      }
      return {
        selectedSquare: square,
        legalMoves: getPremoveMoves(boardFromStore(state), square),
        premove: null,
      };
    });
  },

  premoveStartDrag: (square: Square, offsetX: number, offsetY: number, pointerId?: number) => {
    // Reuse the selection logic, then attach a drag if the piece is a valid pre-move
    // piece with destinations (so the piece follows the pointer; release records it).
    get().premoveSelectSquare(square);
    if (get().selectedSquare === square) {
      set({
        dragState: { square, offsetX, offsetY, pointerId: pointerId ?? null },
        dragPosition: { x: offsetX, y: offsetY },
      });
    }
  },

  setPremove: (premove: Premove) => {
    set({ premove, selectedSquare: null, legalMoves: [], dragState: null, dragPosition: null });
  },

  clearPremove: () => {
    set({ premove: null, selectedSquare: null, legalMoves: [], dragState: null, dragPosition: null });
  },

  startDrag: (square: Square, offsetX: number, offsetY: number, pointerId?: number) => {
    // Vedi selectSquare: il richiamo si accende fuori dall'updater.
    let rejected = false;
    set((state) => {
      // Clear selection helper
      const clearSelection = {
        selectedSquare: null,
        legalMoves: [] as Move[],
        dragState: null,
        dragPosition: null,
      };

      // Special case: drag during capture in progress
      if (state.captureInProgress && square === state.captureInProgress.currentSquare) {
        return {
          dragState: { square, offsetX, offsetY, pointerId: pointerId ?? null },
          dragPosition: { x: offsetX, y: offsetY },
          selectedSquare: square,
          // Keep legalMoves empty - endDrag will handle it specially
        };
      }

      // Check if square has a piece of current player
      const piece = state.pieces.find((p) => p.square === square);
      if (!piece || piece.color !== state.currentTurn) {
        log.info('Game', 'startDrag rejected: not own piece', { square });
        rejected = true;
        return { ...clearSelection, movableHint: null };
      }

      // Calculate legal moves using core engine (carry Frisian state for blocked-king filtering)
      const boardState = boardFromStore(state);

      // Get all legal moves to check if capture is required
      const allLegal = getAllLegalMoves(boardState);
      const hasCaptures = allLegal.some((m) => m.captures.length > 0);

      // If captures are required, only pieces that can capture should be draggable
      let legalMoves: Move[];
      if (hasCaptures) {
        legalMoves = allLegal.filter((m) => m.from === square);
        if (legalMoves.length === 0) {
          log.info('Game', 'startDrag rejected: no legal capture for piece', { square });
          rejected = true;
          return { ...clearSelection, movableHint: null };
        }
      } else {
        // Derive from allLegal (blocked-king filter already applied); getLegalMovesForPiece skips it.
        legalMoves = allLegal.filter((m) => m.from === square);
        if (legalMoves.length === 0) {
          log.info('Game', 'startDrag rejected: no legal moves for piece', { square });
          rejected = true;
          return { ...clearSelection, movableHint: null };
        }
      }

      log.info('Game', 'startDrag ok', { square, legalMoves: legalMoves.length });
      return {
        dragState: { square, offsetX, offsetY, pointerId: pointerId ?? null },
        dragPosition: { x: offsetX, y: offsetY },
        selectedSquare: square,
        legalMoves,
        movableHint: null,
      };
    });
    if (rejected) get().flashMovableHint();
  },

  updateDragPosition: (x: number, y: number) => {
    set({ dragPosition: { x, y } });
  },

  endDrag: (targetSquare: Square | null) => {
    // A drag that finishes a multi-capture on its exact final hop finalizes here
    // (endDrag is only ever called mid-capture). Capture whether it finalized/
    // promoted so we can play the move sound after set() — previously this path
    // was silent.
    let dragFinalized = false;
    /* Come in `executeStep`: il salto appena disegnato porta la corona? */
    let suonaCorona = false;
    /*
     * Delle uscite di `endDrag` ne disegnano un salto DUE: la sequenza che si conclude e
     * quella che avanza di un passo. Sono le uniche vive — l'unico chiamante
     * (`handlePointerUp` in GameBoard) entra qui solo con una presa gia' in corso, come
     * dice il commento qui sopra — e sono le uniche che suonano. I rami che rinunciano
     * (bersaglio non valido, rilascio sulla stessa casella) restano muti, e quelli sotto
     * `captureInProgress` sono irraggiungibili: non si sonorizza codice morto.
     */
    let saltoDisegnato = false;
    let mossaCompletata: Move | null = null;
    set((state) => {
      if (!state.dragState || !targetSquare) {
        return {
          dragState: null,
          dragPosition: null,
        };
      }

      // Special case: drag during capture in progress
      if (state.captureInProgress) {
        const { possibleMoves, currentStep, capturedSoFar, originalFrom } = state.captureInProgress;

        // If dropped on same square, just stop drag
        if (targetSquare === state.dragState.square) {
          return {
            dragState: null,
            dragPosition: null,
          };
        }

        // Filter moves that have targetSquare at the current step
        const matchingMoves = possibleMoves.filter((m) => {
          const path = m.path;
          if (!path || currentStep >= path.length) return false;
          return path[currentStep] === targetSquare;
        });

        if (matchingMoves.length > 0) {
          const firstMove = matchingMoves[0]!;
          const nextCapture = firstMove.captures[currentStep]?.square;
          if (nextCapture !== undefined) {
            const newCapturedSoFar = [...capturedSoFar, nextCapture];

            // Check if this is the last step for all remaining moves
            const allAtLastStep = matchingMoves.every((m) =>
              m.path && currentStep === m.path.length - 1
            );

            saltoDisegnato = true;

            const pezzoInViaggio = state.pieces.find((p) => p.square === originalFrom);
            const corona = pezzoInViaggio
              ? promotionHopInPassing(firstMove, pezzoInViaggio, state.variant)
              : null;
            // Ternario e non `||`, per la ragione scritta in `executeStep`.
            suonaCorona = corona !== null ? corona === currentStep : allAtLastStep && firstMove.isPromotion;

            if (allAtLastStep) {
              dragFinalized = true;
              const newPieces = state.pieces
                .filter((p) => !firstMove.captures.some((c) => c.square === p.square))
                .map((p) => {
                  if (p.square === originalFrom) {
                    return {
                      color: p.color,
                      type: firstMove.isPromotion ? PieceType.KING : p.type,
                      square: targetSquare,
                    };
                  }
                  return p;
                });

              const completedMove: Move = {
                from: firstMove.from,
                to: targetSquare,
                captures: firstMove.captures,
                isPromotion: firstMove.isPromotion,
                path: firstMove.path,
              };
              mossaCompletata = completedMove;

              const newTurn = state.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
              const newMoveHistory = [...state.moveHistory, completedMove];

              // FIX-80: Update positionHistory for threefold detection
              const newBoardState: BoardState = {
                pieces: newPieces,
                currentTurn: newTurn,
                status: GameStatus.PLAYING,
                variant: state.variant,
                ruleMode: state.ruleMode,
                moveHistory: newMoveHistory,
                halfMoveClock: 0,
                positionHistory: state.positionHistory ?? [],
                kingMoveStreak: state.kingMoveStreak,
                frisianEndgamePly: state.frisianEndgamePly,
              };
              const positionHash = serializePosition(newBoardState);
              const newPositionHistory = [...(state.positionHistory ?? []), positionHash];
              newBoardState.positionHistory = newPositionHistory;
              const movingType = pezzoInViaggio?.type ?? PieceType.MAN;
              updateFrisianState(boardFromStore(state), newBoardState, completedMove, movingType);
              const { status: newStatus, reason: newEndReason } = determineGameOutcome(newBoardState);

              return {
                pieces: newPieces,
                currentTurn: newTurn,
                status: newStatus,
                endReason: newEndReason,
                moveHistory: newMoveHistory,
                halfMoveClock: 0,
                positionHistory: newPositionHistory,
                kingMoveStreak: newBoardState.kingMoveStreak,
                frisianEndgamePly: newBoardState.frisianEndgamePly,
                selectedSquare: null,
                legalMoves: [],
                dragState: null,
                dragPosition: null,
                captureInProgress: null,
              };
            }

            // Move to next step
            return {
              captureInProgress: {
                possibleMoves: matchingMoves,
                currentStep: currentStep + 1,
                currentSquare: targetSquare,
                capturedSoFar: newCapturedSoFar,
                originalFrom,
              },
              selectedSquare: targetSquare,
              dragState: null,
              dragPosition: null,
            };
          }
        }

        // Invalid target during capture - cancel drag
        return {
          dragState: null,
          dragPosition: null,
        };
      }

      // Find if target square is a valid move destination
      // For multi-captures, check first intermediate step (path[0])
      const validMove = state.legalMoves.find((m) => {
        if (m.path && m.path.length > 1) {
          return m.path[0] === targetSquare;
        }
        return m.to === targetSquare;
      });

      if (validMove) {
        // Check if this is a multi-capture that needs step-by-step
        if (validMove.path && validMove.path.length > 1) {
          const firstStep = validMove.path[0]!;
          const firstCapture = validMove.captures[0]!.square;

          // Find ALL moves that start with the same first step
          const matchingMoves = state.legalMoves.filter((m) =>
            m.path && m.path.length > 1 && m.path[0] === firstStep
          );

          return {
            captureInProgress: {
              possibleMoves: matchingMoves.length > 0 ? matchingMoves : [validMove],
              currentStep: 1,
              currentSquare: firstStep,
              capturedSoFar: [firstCapture],
              originalFrom: validMove.from,
            },
            selectedSquare: firstStep,
            legalMoves: [] as Move[],
            dragState: null,
            dragPosition: null,
          };
        }

        // Simple move or single capture - execute immediately
        const movingPiece = state.pieces.find((p) => p.square === validMove.from);
        const newPieces = state.pieces
          .filter((p) => !validMove.captures.some((c) => c.square === p.square))
          .map((p) => {
            if (p.square === validMove.from) {
              return {
                color: p.color,
                type: validMove.isPromotion ? PieceType.KING : p.type,
                square: validMove.to,
              };
            }
            return p;
          });

        const newTurn = state.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
        const newMoveHistory = [...state.moveHistory, validMove];
        // FIX-88: Reset for captures OR man moves (matches core engine)
        const newHalfMoveClock = (validMove.captures.length > 0 || (movingPiece && movingPiece.type === PieceType.MAN)) ? 0 : state.halfMoveClock + 1;

        // FIX-80: Update positionHistory for threefold detection
        const newBoardState: BoardState = {
          pieces: newPieces,
          currentTurn: newTurn,
          status: GameStatus.PLAYING,
          variant: state.variant,
          ruleMode: state.ruleMode,
          moveHistory: newMoveHistory,
          halfMoveClock: newHalfMoveClock,
          positionHistory: state.positionHistory ?? [],
          kingMoveStreak: state.kingMoveStreak,
          frisianEndgamePly: state.frisianEndgamePly,
        };
        const positionHash = serializePosition(newBoardState);
        const newPositionHistory = [...(state.positionHistory ?? []), positionHash];
        newBoardState.positionHistory = newPositionHistory;
        updateFrisianState(boardFromStore(state), newBoardState, validMove, movingPiece?.type ?? PieceType.MAN);
        const { status: newStatus, reason: newEndReason } = determineGameOutcome(newBoardState);

        return {
          pieces: newPieces,
          currentTurn: newTurn,
          status: newStatus,
          endReason: newEndReason,
          moveHistory: newMoveHistory,
          halfMoveClock: newHalfMoveClock,
          positionHistory: newPositionHistory,
          kingMoveStreak: newBoardState.kingMoveStreak,
          frisianEndgamePly: newBoardState.frisianEndgamePly,
          selectedSquare: null,
          legalMoves: [],
          dragState: null,
          dragPosition: null,
          captureInProgress: null,
        };
      }

      // Invalid target, cancel drag
      return {
        dragState: null,
        dragPosition: null,
        selectedSquare: null,
        legalMoves: [],
      };
    });
    if (saltoDisegnato) {
      // Trascinata o toccata, una presa suona `move` a ogni salto: il gesto conta per la
      // mossa semplice (`move-drag`), non per la presa. E la damatura cade nello stesso
      // salto in cui cadrebbe toccando: la stessa presa non puo' suonare in due modi a
      // seconda del gesto.
      log.info('Game', 'endDrag → suono del salto', { dragFinalized, suonaCorona });
      playCaptureHop(suonaCorona);
      if (dragFinalized) vibrate(40);
    }
    return mossaCompletata;
  },

  stopDrag: () => {
    set({
      dragState: null,
      dragPosition: null,
    });
  },

  cancelDrag: () => {
    set({
      dragState: null,
      dragPosition: null,
      selectedSquare: null,
      legalMoves: [],
    });
  },

  findDirectMove: (square: Square): DirectTapResult | null => {
    const state = get();

    const boardState = boardFromStore(state);

    const allLegal = getAllLegalMoves(boardState);
    // Only auto-resolve when captures are FORCED — never auto-play a quiet move from one tap.
    if (!allLegal.some((m) => m.captures.length > 0)) return null;

    // Captures landing on the tapped square. Bail if it isn't a capture landing.
    const landing = allLegal.filter((m) => m.to === square);
    if (landing.length === 0) return null;

    const sources = [...new Set(landing.map((m) => m.from))];
    if (sources.length > 1) {
      // Several pieces can land here: highlight them so the player picks which one.
      set({ captureSourceChoices: sources, pendingCaptureTarget: square });
      return { kind: 'choose', to: square, froms: sources };
    }

    const from = sources[0]!;
    const moves = allLegal.filter((m) => m.from === from);
    // Select the piece: highlights it and lets makeMove gather alternative lines.
    set({ selectedSquare: from, legalMoves: moves, captureSourceChoices: [], pendingCaptureTarget: null, movableHint: null });
    return { kind: 'direct', from, moves };
  },

  // Pick which piece performs an ambiguous capture: select it (with its capture
  // moves) and clear the choice highlights. Returns its moves so the caller can
  // immediately play toward the pending target.
  selectCaptureSource: (from: Square): Move[] => {
    const state = get();
    const boardState = boardFromStore(state);
    const moves = getAllLegalMoves(boardState).filter((m) => m.from === from);
    set({ selectedSquare: from, legalMoves: moves, captureSourceChoices: [], pendingCaptureTarget: null, movableHint: null });
    return moves;
  },

  clearCaptureChoices: () => {
    set({ captureSourceChoices: [], pendingCaptureTarget: null, movableHint: null });
  },

  flashMovableHint: (squares?: Square[]) => {
    // A partita finita non si indica niente. La selezione di un pezzo invece continua a
    // funzionare (comportamento preesistente), ma "ecco cosa puoi muovere" a partita chiusa
    // sarebbe una risposta sbagliata: su una patta per regola le mosse legali esistono ancora.
    if (get().status !== GameStatus.PLAYING) return;
    // Senza argomento le sorgenti si ricalcolano qui invece di essere passate dai rami che
    // hanno già `allLegal`: costa una generazione di mosse su un tocco che oggi non produce
    // NIENTE, e in cambio i quattro punti di accensione condividono una firma sola.
    const target = squares ?? movableSquares(getAllLegalMoves(boardFromStore(get())));
    if (target.length === 0) return; // partita finita: non c'è niente da indicare
    const nonce = ++movableHintSeq;
    set({ movableHint: { squares: target, nonce } });
    armMovableHintTimer(nonce);
  },

  clearMovableHint: () => {
    set({ movableHint: null });
  },

  setGameMode: (mode: GameMode) => {
    aiGeneration++; // invalidate any AI result still in flight
    cancelCaptureReplayNow();
    set((state) => {
      // Ripartenza pulita: regole standard e mossa libera. Le partite con modalità o
      // apertura scelte passano da startLocalGame, non da qui.
      const newBoard = createInitialBoard(state.variant, DEFAULT_GAME_MODE);
      return {
        pieces: newBoard.pieces,
        currentTurn: newBoard.currentTurn,
        status: GameStatus.PLAYING,
        moveHistory: [],
        halfMoveClock: 0,
        positionHistory: [],
        kingMoveStreak: undefined,
        frisianEndgamePly: null,
        startBoard: null, // back to the standard opening
        ruleMode: DEFAULT_GAME_MODE,
        openingMode: DEFAULT_OPENING_MODE,
        openingPlyCount: 0,
        drawnOpening: null,
        endReason: null,
        selectedSquare: null,
        legalMoves: [],
        captureInProgress: null,
        premove: null,
        captureSourceChoices: [],
        pendingCaptureTarget: null,
        movableHint: null,
        gameMode: mode,
        isAIThinking: false,
        localPlayerColor: Color.WHITE,
        drawOffered: false,
        aiDeclinedDraw: false,
        drawTimeoutId: null,
      };
    });
  },

  setVariant: (variant: GameVariant) => {
    aiGeneration++; // invalidate any AI result still in flight
    cancelCaptureReplayNow();
    // Come setGameMode: cambiare variante riporta a regole standard e mossa libera.
    const newBoard = createInitialBoard(variant, DEFAULT_GAME_MODE);
    set({
      pieces: newBoard.pieces,
      currentTurn: newBoard.currentTurn,
      status: GameStatus.PLAYING,
      variant,
      moveHistory: [],
      halfMoveClock: 0,
      positionHistory: [],
      kingMoveStreak: undefined,
      frisianEndgamePly: null,
      startBoard: null, // back to the standard opening
      ruleMode: DEFAULT_GAME_MODE,
      openingMode: DEFAULT_OPENING_MODE,
      openingPlyCount: 0,
      drawnOpening: null,
      endReason: null,
      selectedSquare: null,
      legalMoves: [],
      captureInProgress: null,
      premove: null,
      captureSourceChoices: [],
      pendingCaptureTarget: null,
      movableHint: null,
      isAIThinking: false,
      drawOffered: false,
      aiDeclinedDraw: false,
      drawTimeoutId: null,
    });
  },

  startLocalGame: (variant: GameVariant, mode: GameMode, timeControl: TimeControlKey, options?: LocalGameOptions) => {
    useBlindStore.getState().resetBlindSession();
    log.info('Game', 'startGame() called', { variant, mode, timeControl, options });
    aiGeneration++; // invalidate any AI result still in flight
    cancelCaptureReplayNow();
    const ruleMode = options?.ruleMode ?? DEFAULT_GAME_MODE;
    // Il criterio si risolve QUI contro la variante: un ballot chiesto su una variante
    // senza tabella federale diventa 'random3', ed è quello che la rivincita ripescherà.
    const openingMode = resolveOpeningMode(options?.opening ?? DEFAULT_OPENING_MODE, variant);
    const applied = applyOpening(createInitialBoard(variant, ruleMode), openingMode);
    const newBoard = applied.board;
    const newClockState = createClockState(timeControl);
    set({
      pieces: newBoard.pieces,
      currentTurn: newBoard.currentTurn,
      // Dallo stato calcolato dal motore, non da un PLAYING dato per scontato: le
      // semi-mosse dell'apertura sono mosse vere e passano da determineGameStatus.
      status: newBoard.status,
      variant,
      gameMode: mode,
      ruleMode,
      openingMode,
      openingPlyCount: applied.plies,
      drawnOpening: applied.banner,
      // L'andata e ritorno si gioca con ogni apertura: col sorteggio il ritorno
      // rigioca la stessa pescata dall'altra parte, con la mossa libera riparte
      // dalla posizione iniziale — che e' esattamente cio' che `newBoard` e' li'.
      matchLegs: (options?.legs ?? 1) as MatchLegs,
      matchLegIndex: 1 as 1 | 2,
      legOpeningBoard: (options?.legs ?? 1) === 2 ? newBoard : null,
      endReason: null,
      // Le semi-mosse dell'apertura restano in cronologia, come lato server: si vedono
      // nella lista mosse e la review può risalirci dentro.
      moveHistory: newBoard.moveHistory,
      halfMoveClock: newBoard.halfMoveClock,
      positionHistory: newBoard.positionHistory ?? [],
      kingMoveStreak: newBoard.kingMoveStreak,
      frisianEndgamePly: newBoard.frisianEndgamePly ?? null,
      startBoard: null, // back to the standard opening
      selectedSquare: null,
      legalMoves: [],
      captureInProgress: null,
      // Come in resetGame: una presa non attraversa l'inizio di una partita nuova.
      dragState: null,
      dragPosition: null,
      premove: null,
      captureSourceChoices: [],
      pendingCaptureTarget: null,
      movableHint: null,
      clockState: newClockState,
      timeControlPreset: timeControl,
      // Default Bianco, non 'random' come nella sfida fra umani: contro il computer
      // l'umano ha sempre giocato il Bianco, e chi non sceglie non deve cambiare lato.
      localPlayerColor: resolveHumanColor(options?.color ?? 'white'),
      isAIThinking: false,
      reviewIndex: null,
      reviewPieces: null,
      reviewKingMoveStreak: null,
      reviewTurn: null,
      drawOffered: false,
      aiDeclinedDraw: false,
      drawTimeoutId: null,
      startedAtMs: Date.now(),
    });
  },

  startLocalGameFromBoard: (board, mode, timeControl, humanColor, options) => {
    useBlindStore.getState().resetBlindSession();
    log.info('Game', 'startLocalGameFromBoard() called', { variant: board.variant, mode, timeControl, humanColor, options });
    aiGeneration++; // invalidate any AI result still in flight
    cancelCaptureReplayNow();
    const newClockState = createClockState(timeControl);
    // Default: la modalità della posizione di partenza. È ciò che tiene identico il
    // proseguimento di un puzzle (board sempre 'normal') dopo l'aggiunta delle opzioni.
    const ruleMode = options?.ruleMode ?? board.ruleMode ?? DEFAULT_GAME_MODE;
    set({
      pieces: board.pieces,
      currentTurn: board.currentTurn,
      status: board.status,
      variant: board.variant,
      gameMode: mode,
      ruleMode,
      // Una posizione custom non ha apertura sorteggiata: niente da ripescare né da mostrare.
      openingMode: DEFAULT_OPENING_MODE,
      openingPlyCount: 0,
      drawnOpening: null,
      endReason: null,
      // Treat the continuation as a fresh game FROM this position (no inherited
      // move log / draw counters / repetition history). Remember the origin: review
      // and takeback replay moveHistory from here, and replaying it from the standard
      // opening instead would make the very first move illegal (applyMove throws).
      // The seed board (e.g. a solved-puzzle continuation) still carries the puzzle's
      // own moveHistory/positionHistory — clear both so the replayed board matches the
      // live game's clean slate. Otherwise the leftover position hashes inflate the
      // threefold-repetition count, review reconstructs a phantom draw, and the next
      // replayed move throws "Invalid move … not legal".
      // La modalità va scritta anche QUI: la review e il takeback rigiocano da questa
      // board, e senza rigiocherebbero la partita con le regole standard.
      startBoard: { ...board, ruleMode, halfMoveClock: 0, moveHistory: [], positionHistory: [] },
      moveHistory: [],
      halfMoveClock: 0,
      positionHistory: [],
      // Carry the position's Frisian state (usually absent for puzzle boards); the 2K-vs-1
      // counter then restarts from this continuation, consistent with the reset move log.
      kingMoveStreak: board.kingMoveStreak,
      frisianEndgamePly: board.frisianEndgamePly ?? null,
      selectedSquare: null,
      legalMoves: [],
      captureInProgress: null,
      // Come in resetGame: una presa non attraversa l'inizio di una partita nuova.
      dragState: null,
      dragPosition: null,
      premove: null,
      captureSourceChoices: [],
      pendingCaptureTarget: null,
      movableHint: null,
      clockState: newClockState,
      timeControlPreset: timeControl,
      localPlayerColor: humanColor,
      isAIThinking: false,
      reviewIndex: null,
      reviewPieces: null,
      reviewKingMoveStreak: null,
      reviewTurn: null,
      drawOffered: false,
      aiDeclinedDraw: false,
      drawTimeoutId: null,
      startedAtMs: Date.now(),
    });
  },

  makeAIMove: () => {
    log.info('Game', 'aiMove() called');
    set((state) => {
      // Don't make AI move if game is not in progress or not AI's turn
      if (state.status !== GameStatus.PLAYING) { log.info('Game', 'makeAIMove skipped', { reason: 'not-playing', status: state.status }); return state; }
      if (!isAIGameMode(state.gameMode)) { log.info('Game', 'makeAIMove skipped', { reason: 'no-ai', gameMode: state.gameMode }); return state; }
      const aiColor = state.localPlayerColor === Color.WHITE ? Color.BLACK : Color.WHITE;
      if (state.currentTurn !== aiColor) { log.info('Game', 'makeAIMove skipped', { reason: 'not-ai-turn', currentTurn: state.currentTurn, aiColor }); return state; }
      if (state.isAIThinking) { log.info('Game', 'makeAIMove skipped', { reason: 'already-thinking' }); return state; }
      if (state.captureInProgress) { log.info('Game', 'makeAIMove skipped', { reason: 'capture-in-progress' }); return state; }
      if (state.reviewIndex !== null) { log.info('Game', 'makeAIMove skipped', { reason: 'review', reviewIndex: state.reviewIndex }); return state; } // Don't move during review

      return { isAIThinking: true };
    });

    // Helper to apply AI move result to store
    const applyAIResult = (aiMove: Move | null, thinkTime: number, myGen: number) => {
      // Ignore a stale result: the position moved on (takeback / rematch / variant
      // change) and a newer request — or none — now owns the AI's turn.
      if (myGen !== aiGeneration) {
        log.info('Game', 'stale AI result dropped (generation mismatch)', { myGen, aiGeneration, from: aiMove?.from, to: aiMove?.to });
        return;
      }
      const state = useGameStore.getState();
      if (!state.isAIThinking) {
        log.info('Game', 'AI result dropped (not thinking)', { from: aiMove?.from, to: aiMove?.to });
        return;
      }

      log.info('Game', 'aiMove() calculated', { from: aiMove?.from, to: aiMove?.to, thinkTime });

      if (!aiMove) {
        set({ isAIThinking: false, aiTimeoutId: null });
        return;
      }

      // Guard against applying a result computed for a board that no longer
      // matches the live position (a stale worker response that slipped past the
      // generation check coincidentally, or an entered review). Drop it WITHOUT
      // flipping the turn or appending history: LocalGame's trigger effect fires
      // on isAIThinking → false and recomputes for the current board (self-heal).
      if (state.reviewIndex !== null || !isAIMoveStillLegal(
        boardFromStore(state),
        aiMove,
      )) {
        log.info('Game', 'aiMove() result stale/illegal, discarding', { from: aiMove.from, to: aiMove.to, reviewIndex: state.reviewIndex, moveCount: state.moveHistory.length });
        set({ isAIThinking: false, aiTimeoutId: null });
        return;
      }

      // Deduct thinking time from AI's clock — only if a time control is set.
      // Without this guard, vs-AI games with timeControlPreset='none' (clock starts at 0)
      // would flag-fall the computer on its first move.
      if (state.timeControlPreset !== 'none') {
        const aiColor = state.currentTurn;
        const clockKey = aiColor === Color.WHITE ? 'white' : 'black';
        const currentTime = state.clockState[clockKey].timeRemaining;
        const newTime = Math.max(0, currentTime - thinkTime);
        set((prevState) => ({
          clockState: {
            ...prevState.clockState,
            [clockKey]: {
              ...prevState.clockState[clockKey],
              timeRemaining: newTime,
            },
          },
        }));

        if (newTime <= 0) {
          log.info('Game', 'AI flag-fall on own clock', { aiColor, thinkTime });
          const winner = aiColor === Color.WHITE ? Color.BLACK : Color.WHITE;
          set({
            status: winner === Color.WHITE ? GameStatus.WHITE_WINS : GameStatus.BLACK_WINS,
            isAIThinking: false,
            aiTimeoutId: null,
          });
          return;
        }
      }

      // Apply the AI move
      // La presa multipla dell'AI si rigioca salto per salto come quella del giocatore.
      // La damiera vera la scrive comunque il `set` qui sotto, subito: quello che segue
      // e' solo un fotogramma che scorre sopra.
      stopCaptureReplayTimer();
      let aiReplay = false;
      set((state) => {
        const movingPiece = state.pieces.find((p) => p.square === aiMove.from);
        const newPieces = state.pieces
          .filter((p) => !aiMove.captures.some((c) => c.square === p.square))
          .map((p) => {
            if (p.square === aiMove.from) {
              return {
                color: p.color,
                type: aiMove.isPromotion ? PieceType.KING : p.type,
                square: aiMove.to,
              };
            }
            return p;
          });

        const newTurn = state.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
        const newMoveHistory = [...state.moveHistory, aiMove];
        // FIX-88: Reset for captures OR man moves
        const newHalfMoveClock = (aiMove.captures.length > 0 || (movingPiece && movingPiece.type === PieceType.MAN)) ? 0 : state.halfMoveClock + 1;

        // FIX-80: Update positionHistory for threefold detection
        const newBoardState: BoardState = {
          pieces: newPieces,
          currentTurn: newTurn,
          status: GameStatus.PLAYING,
          variant: state.variant,
          ruleMode: state.ruleMode,
          moveHistory: newMoveHistory,
          halfMoveClock: newHalfMoveClock,
          positionHistory: state.positionHistory ?? [],
          kingMoveStreak: state.kingMoveStreak,
          frisianEndgamePly: state.frisianEndgamePly,
        };
        const positionHash = serializePosition(newBoardState);
        const newPositionHistory = [...(state.positionHistory ?? []), positionHash];
        newBoardState.positionHistory = newPositionHistory;
        updateFrisianState(boardFromStore(state), newBoardState, aiMove, movingPiece?.type ?? PieceType.MAN);
        const { status: newStatus, reason: newEndReason } = determineGameOutcome(newBoardState);

        // Keep a pre-move source selection alive across the AI's move (chess.com-style),
        // recomputed as real legal moves now that it's the human's turn — parity with the
        // online path in syncFromExternal. A committed pre-move (selectedSquare === null) is
        // untouched here and consumed by the pre-move effect in GameBoard.tsx.
        const selection = recomputeSelection({ ...newBoardState, status: newStatus }, state.selectedSquare, state.captureInProgress);

        aiReplay = state.reviewIndex === null
          && !state.captureInProgress
          && !!aiMove.path
          && aiMove.path.length > 1
          && aiMove.captures.length >= aiMove.path.length;

        return {
          pieces: newPieces,
          currentTurn: newTurn,
          status: newStatus,
          endReason: newEndReason,
          moveHistory: newMoveHistory,
          halfMoveClock: newHalfMoveClock,
          positionHistory: newPositionHistory,
          kingMoveStreak: newBoardState.kingMoveStreak,
          frisianEndgamePly: newBoardState.frisianEndgamePly,
          ...selection,
          isAIThinking: false,
          aiTimeoutId: null,
          // The AI moving supersedes any pending draw offer / decline notice.
          drawOffered: false,
          aiDeclinedDraw: false,
          ...(aiReplay ? { captureReplay: { pieces: state.pieces, move: aiMove, step: 1 } } : { captureReplay: null }),
        };
      });
      // Feedback on the AI move — mirrors the human path (makeMove/executeStep)
      // and the online opponent path (setGameUpdate). Both helpers no-op when
      // the user has disabled sound/vibration in preferences.
      //
      // Quando il replay parte, il suono NON si emette qui: la presa ne fa uno per salto
      // e il primo lo suona `avviaReplayPresa`, che sa anche se la dama nasce proprio la'.
      // Suonarlo anche qui vorrebbe dire due suoni sul primo salto — e su una presa che
      // promuove sarebbero due suoni DIVERSI, a un istante di distanza.
      log.info('Game', 'applyAIResult → move sound', { from: aiMove.from, to: aiMove.to, aiReplay });
      if (aiReplay) {
        avviaReplayPresa(true);
      } else {
        // AI move is never a local tap/drag gesture
        playMoveSound(aiMove.isPromotion, 'auto', aiMove.captures.length > 0);
      }
      vibrate(40);

      // A queued pre-move is NOT played here. GameBoard consumes it once it's the
      // human's turn again, replaying it through the same animated capture path the
      // player's own moves use (deferred to the next render so the AI move shows
      // first). Playing it here synchronously with `immediate=true` scrambled
      // pieces on captures — see the pre-move effect in GameBoard.tsx.

      // Log game end if it occurred
      const finalState = useGameStore.getState();
      if (finalState.status !== GameStatus.PLAYING) {
        log.info('Game', 'game ended', { status: finalState.status });
      }
    };

    // Use setTimeout to allow UI to update before AI thinks
    const timeoutId = setTimeout(() => {
      const state = useGameStore.getState();
      if (!state.isAIThinking) return;

      // Tag this dispatch. Captured here (not at makeAIMove entry) so re-entry
      // while a legit think is running doesn't invalidate it; applyAIResult drops
      // any response whose id no longer matches (a later dispatch or a reset).
      const myGen = ++aiGeneration;
      log.info('Game', 'aiMove dispatch', { myGen, currentTurn: state.currentTurn, moveCount: state.moveHistory.length });

      // AI search root — must carry the Frisian state so the engine honours blocked-king / 2K-vs-1.
      const boardState = boardFromStore(state);

      // TS minimax path, indebolito secondo il livello scelto.
      const level = AI_LEVELS[state.gameMode];
      if (!level) {
        // 'pvp': nessuna AI. Irraggiungibile in pratica (la guardia in testa a
        // makeAIMove non accende isAIThinking), ma il tipo è nullable e senza
        // questo ramo si resterebbe a "sta pensando" per sempre.
        log.info('Game', 'aiMove dispatch skipped', { reason: 'no-ai-level', gameMode: state.gameMode });
        set({ isAIThinking: false, aiTimeoutId: null });
        return;
      }
      const profile = AI_STRENGTH_PROFILES[level];

      // Try Web Worker first, fall back to synchronous
      const worker = getAIWorker();
      if (worker) {
        const thinkStart = Date.now();
        worker.onmessage = (e: MessageEvent<AIWorkerResponse>) => {
          const thinkTime = Date.now() - thinkStart;
          applyAIResult(e.data.move, thinkTime, myGen);
        };
        worker.onerror = () => {
          // Fallback to synchronous on worker error
          log.info('Game', 'Worker error, falling back to sync');
          const syncStart = Date.now();
          const aiMove = findBestMoveWeakened(boardState, profile);
          applyAIResult(aiMove, Date.now() - syncStart, myGen);
        };
        worker.postMessage({ board: boardState, level } satisfies AIWorkerRequest);
      } else {
        // Synchronous fallback (no Web Worker support)
        const thinkStart = Date.now();
        const aiMove = findBestMoveWeakened(boardState, profile);
        applyAIResult(aiMove, Date.now() - thinkStart, myGen);
      }
    }, 100);
    // FIX-92: Store timeout ID for cleanup
    set({ aiTimeoutId: timeoutId });
  },

  resign: () => {
    log.info('Game', 'resign() called');
    set((state) => {
      if (state.status !== GameStatus.PLAYING) return {};
      // In presenza si arrende chi ha il tratto: e' l'unico dei due che in quel
      // momento ha in mano il dispositivo, e "il giocatore locale" non esiste.
      // Contro il computer resta SEMPRE l'umano: durante il pensiero dell'AI
      // `currentTurn` e' il colore dell'AI, e abbandoneremmo al posto suo.
      //
      // LA PRESENZA NON PASSA PIU' DA QUI: la sua schermata chiude la partita
      // senza vincitore (`abandonGame` qui sotto), perche' fra due persone a
      // questo dispositivo non c'e' nessuno a cui consegnarla. Il ramo resta
      // perche' questa e' un'azione pubblica dello store: se un domani qualcuno
      // la richiamasse in presenza, meglio il lato giusto di un lato inventato.
      const loser = isHotseatMode(state.gameMode) ? state.currentTurn : state.localPlayerColor;
      return {
        status: loser === Color.WHITE ? GameStatus.BLACK_WINS : GameStatus.WHITE_WINS,
        endReason: { kind: 'resignation', side: loser } as LocalEndAction,
      };
    });
  },

  agreeDraw: () => {
    log.info('Game', 'agreeDraw() called');
    set((state) => {
      // Solo in presenza: i due giocatori sono qui e si sono gia' accordati a voce,
      // non c'e' nessuno a cui mandare una proposta. Online la patta resta una
      // trattativa, e contro il computer decide `offerDraw`.
      if (!isHotseatMode(state.gameMode)) return {};
      if (state.status !== GameStatus.PLAYING) return {};
      if (state.reviewIndex !== null) return {};
      return {
        status: GameStatus.DRAW,
        endReason: { kind: 'agreement' } as LocalEndAction,
        drawOffered: false,
      };
    });
  },

  abandonGame: () => {
    log.info('Game', 'abandonGame() called');
    set((state) => {
      // Solo in presenza. Qui "abbandonare" non e' arrendersi: i due giocatori sono
      // le stesse mani sullo stesso telefono, e consegnare la partita a uno dei due
      // colori vorrebbe dire inventare un vincitore. La partita si chiude e basta.
      if (!isHotseatMode(state.gameMode)) return {};
      if (state.status !== GameStatus.PLAYING) return {};
      /*
       * Nessuna guardia su `reviewIndex`, al contrario di `agreeDraw`: la barra
       * comandi resta viva mentre si sfogliano le mosse, quindi una guardia qui
       * lascerebbe un bottone che non fa niente e non dice perche'.
       */
      return {
        // Lo stato c'era dal primo giorno e nessuno lo scriveva.
        status: GameStatus.ABANDONED,
        // Il nome e' quello del server per la stessa fine, come vuole LocalEndAction.
        endReason: { kind: 'aborted' } as LocalEndAction,
        drawOffered: false,
      };
    });
  },

  // AI mode only: the human offers a draw; the AI evaluates the position and
  // accepts (game ends in a draw) or declines (button re-enables, brief notice).
  offerDraw: () => {
    log.info('Game', 'offerDraw() called');
    const state = useGameStore.getState();
    // In presenza la patta non si PROPONE: i due giocatori sono qui, si accordano a
    // voce e chiudono con `agreeDraw`. Qui resta solo la trattativa col motore.
    if (!isAIGameMode(state.gameMode)) return;
    if (state.status !== GameStatus.PLAYING) return;
    if (state.drawOffered) return;
    if (state.reviewIndex !== null) return;
    if (state.isAIThinking) return;

    if (state.drawTimeoutId) clearTimeout(state.drawTimeoutId);
    set({ drawOffered: true, aiDeclinedDraw: false });

    const timeoutId = setTimeout(() => {
      const s = useGameStore.getState();
      // Offer may have been cancelled by a reset/new game/move.
      if (!s.drawOffered) return;

      const board = boardFromStore(s);
      const aiColor = s.localPlayerColor === Color.WHITE ? Color.BLACK : Color.WHITE;

      if (shouldAcceptDraw(board, aiColor)) {
        log.info('Game', 'AI accepted draw offer');
        set({ status: GameStatus.DRAW, endReason: { kind: 'agreement' }, drawOffered: false, isAIThinking: false, drawTimeoutId: null });
      } else {
        log.info('Game', 'AI declined draw offer');
        const noticeId = setTimeout(() => {
          if (useGameStore.getState().aiDeclinedDraw) set({ aiDeclinedDraw: false, drawTimeoutId: null });
        }, AI_DRAW_DECLINE_NOTICE_MS);
        set({ drawOffered: false, aiDeclinedDraw: true, drawTimeoutId: noticeId });
      }
    }, AI_DRAW_DELAY_MS);
    set({ drawTimeoutId: timeoutId });
  },

  startReturnLeg: () => {
    aiGeneration++;
    cancelCaptureReplayNow();
    log.info('Game', 'startReturnLeg() called');
    set((state) => {
      const seed = state.legOpeningBoard;
      if (!seed || state.matchLegs !== 2 || state.matchLegIndex !== 1) return {};
      if (state.aiTimeoutId) clearTimeout(state.aiTimeoutId);
      if (state.drawTimeoutId) clearTimeout(state.drawTimeoutId);
      terminateAIWorker();

      // Modellato su resetGame, non su startLocalGameFromBoard: quel percorso serve
      // a continuare da una posizione COMPOSTA e azzera moveHistory, openingMode e
      // openingPlyCount. Qui invece l'apertura sorteggiata deve restare — nella
      // lista mosse, e soprattutto come pavimento sotto cui l'annulla non scende.
      return {
        pieces: seed.pieces,
        currentTurn: seed.currentTurn,
        status: seed.status,
        moveHistory: seed.moveHistory,
        halfMoveClock: seed.halfMoveClock,
        positionHistory: seed.positionHistory ?? [],
        kingMoveStreak: seed.kingMoveStreak,
        frisianEndgamePly: seed.frisianEndgamePly ?? null,
        startBoard: null,
        matchLegIndex: 2 as 1 | 2,
        endReason: null,
        selectedSquare: null,
        legalMoves: [],
        captureInProgress: null,
        // Come in resetGame: una presa non attraversa l'inizio di una partita nuova.
        dragState: null,
        dragPosition: null,
        premove: null,
        captureSourceChoices: [],
        pendingCaptureTarget: null,
        movableHint: null,
        reviewIndex: null,
        reviewPieces: null,
        reviewKingMoveStreak: null,
        reviewTurn: null,
        isAIThinking: false,
        aiTimeoutId: null,
        drawOffered: false,
        aiDeclinedDraw: false,
        drawTimeoutId: null,
        clockState: createClockState(state.timeControlPreset),
        // Ri-arma il banner dell'apertura: al ritorno e' la stessa, e va ridetta.
        startedAtMs: Date.now(),
      };
    });
  },

  syncFromExternal: (state: BoardState, opts?: { replay?: boolean; suono?: boolean }) => {
    log.info('Game', 'syncFromExternal() called', { turn: state.currentTurn, moves: state.moveHistory.length });
    // Il replay in corso si annulla SEMPRE e si riapre semmai piu' sotto: una regola sola,
    // senza guardie. Qui non c'e' niente da arbitrare, perche' la premossa non passa piu'
    // di qua: l'effetto in `GameBoard` ASPETTA che il replay si spenga prima di partire
    // (guardia su `captureReplay`), in tutte le modalita'.
    stopCaptureReplayTimer();
    let replayMove: Move | null = null;
    set((prev) => {
      const board: BoardState = {
        pieces: state.pieces,
        currentTurn: state.currentTurn,
        status: state.status,
        variant: state.variant,
        moveHistory: state.moveHistory,
        halfMoveClock: state.halfMoveClock,
        positionHistory: state.positionHistory ?? [],
        // Preserve the server's authoritative Frisian state so the client's own legal-move
        // generation agrees on blocked-king / 2K-vs-1 (otherwise it offers moves the server rejects).
        kingMoveStreak: state.kingMoveStreak,
        frisianEndgamePly: state.frisianEndgamePly,
        ruleMode: state.ruleMode,
      };
      // Keep the selection alive across the opponent's move (or a mid-turn re-sync) instead
      // of clearing it every time. `premove` (a committed pre-move) is left untouched — it
      // is consumed separately by the pre-move effect in GameBoard (one path, all modes).
      const selection = recomputeSelection(board, prev.selectedSquare, prev.captureInProgress);
      // Niente replay se il giocatore ha una presa a meta' per le mani, o se sta
      // guardando indietro: in entrambi i casi la damiera a schermo non e' quella viva.
      replayMove = opts?.replay === false || prev.captureInProgress || prev.reviewIndex !== null
        ? null
        : detectExternalCapture(prev, board);
      return {
        ...board,
        ...selection,
        captureInProgress: null,
        captureReplay: replayMove ? { pieces: prev.pieces, move: replayMove, step: 1 } : null,
        captureSourceChoices: [],
        pendingCaptureTarget: null,
        movableHint: null,
      };
    });
    // `suono` default acceso. Lo spegne solo chi guarda una partita per cui non ha MAI
    // suonato niente — lo spettatore: prese sonore in una partita per il resto muta si
    // sentirebbero come un difetto, non come un di piu'.
    if (replayMove) avviaReplayPresa(opts?.suono !== false);
  },

  cancelCaptureReplay: () => {
    cancelCaptureReplayNow();
  },

  setOnlineStartBoard: (board: BoardState | null) => {
    /*
     * Inerte a riferimento invariato, perche' la stessa scrittura arriva piu'
     * volte: al montaggio della schermata online la board passa DUE volte —
     * una dentro `enterOnlineGame`, una dall'effetto che la riapplica — e le
     * due porte di rientro (`game:rejoined` e la risposta di `check-active`)
     * arrivano spesso entrambe.
     *
     * Non e' una difesa dai ri-disegni: i selettori di zustand confrontano il
     * loro risultato, quindi riscrivere lo stesso riferimento non ridisegnerebbe
     * nessuno comunque. Serve a non rifare lo stato per niente e a tenere onesto
     * il registro, che altrimenti direbbe «scritta» quattro volte per partita.
     *
     * Confronto per IDENTITA' e non per contenuto: ogni payload del server porta
     * un oggetto nuovo, quindi quando la board cambia davvero si scrive sempre;
     * quello che il confronto ferma sono le ripetizioni dello stesso valore,
     * `null` compreso.
     */
    set((state) => {
      if (state.startBoard === board) return {};
      log.info('Game', 'setOnlineStartBoard', { composta: board !== null, pezzi: board?.pieces.length ?? null });
      return { startBoard: board };
    });
  },

  enterOnlineGame: (startBoard: BoardState | null = null) => {
    aiGeneration++; // invalidate any AI result still in flight
    /*
     * E SI SPEGNE IL WORKER, non solo il suo risultato.
     *
     * `aiGeneration++` qui sopra fa buttare via la risposta che arriva tardi,
     * ma NON ferma la ricerca: il minimax dentro il worker arriva in fondo
     * comunque, e il worker resta residente per tutta la partita online. Chi
     * gioca una partita contro il computer e poi ne apre una online — che e'
     * il giro piu' comune che ci sia — si porta dietro un thread che cerca
     * mosse per una partita che non esiste piu'. Su un telefono vecchio e' un
     * core occupato proprio nei primi secondi, quando la partita comincia.
     *
     * `terminateAIWorker()` era chiamata solo da `resetGame` e da
     * `startReturnLeg`: due strade che questa non percorre.
     */
    terminateAIWorker();
    cancelCaptureReplayNow();
    /*
     * NON `setGameMode('pvp')`: quello ricostruisce anche la damiera iniziale, e
     * la damiera di una partita online arriva dal server (`syncFromExternal`).
     * Qui si azzera solo ciò che appartiene alla sessione precedente e che il
     * server non riscriverà da sé.
     */
    set({
      gameMode: 'pvp',
      /*
       * La posizione di partenza di QUESTA partita, e `null` per tutte le altre.
       *
       * Scriverla qui e non solo dopo non e' un di piu': chi monta la schermata
       * online la legge e la passa, cosi' la correttezza non dipende dall'ordine
       * in cui girano due effetti nello stesso componente. L'effetto che la
       * riapplica al cambio di valore serve per le partite che cominciano a
       * schermata GIA' montata (rivincita, ritorno, rientro).
       */
      startBoard,
      reviewIndex: null,
      reviewPieces: null,
      reviewKingMoveStreak: null,
      reviewTurn: null,
      premove: null,
      selectedSquare: null,
      legalMoves: [],
      captureInProgress: null,
      captureSourceChoices: [],
      pendingCaptureTarget: null,
      movableHint: null,
    });
  },

  // Review mode actions
  // Navigate to any ply (view-only, both AI and online). index semantics:
  // null / >= moveHistory.length / < 0 => live; N => position after N moves (0 = initial).
  reviewToIndex: (index: number | null) => {
    set((state) => {
      const totalMoves = state.moveHistory.length;
      log.info('Game', 'reviewToIndex', { from: state.reviewIndex, to: index, total: totalMoves });
      if (index === null || index >= totalMoves || index < 0) {
        // Back to live
        if (state.reviewIndex === null) return {};
        log.info('Game', 'reviewToIndex → live', {});
        return { reviewIndex: null, reviewPieces: null, reviewKingMoveStreak: null, reviewTurn: null, selectedSquare: null, legalMoves: [] };
      }
      // Don't disrupt an in-progress multi-capture
      if (state.captureInProgress) {
        log.info('Game', 'reviewToIndex blocked (mid-capture)', { index });
        return {};
      }

      // Read-only review (viewing a past ply) is always allowed, including during
      // the AI's turn/thinking: LocalGame pauses AI dispatch while reviewing, and
      // any in-flight AI result is dropped by applyAIResult once reviewIndex !==
      // null, so the board can't change under the review. A pick-up in AI mode
      // commits a takeback via commitTakeback, which cancels the AI's in-flight
      // work — so history stays consistent without over-blocking navigation.

      // Track first entry into review mode
      if (state.reviewIndex === null) trackReviewModeUsed();

      /*
       * Reconstruct board at index, from wherever this game began.
       *
       * Il try/catch non e' pessimismo decorativo: `applyMove` LANCIA su una
       * mossa che non e' legale nella posizione ricostruita, e siamo dentro
       * l'updater di zustand chiamato da un onClick — l'eccezione non ha nessun
       * confine prima dell'ErrorBoundary di `main.tsx`, che sostituisce l'app
       * intera con «Something went wrong» mentre la partita a tempo continua sul
       * server. Una revisione che non si puo' ricostruire non e' un motivo per
       * perdere la partita: si resta sulla damiera viva.
       *
       * Le cause note sono due, entrambe riparate. Uno `startBoard` stantio
       * (azzerato da `enterOnlineGame`) e una partita online nata da una
       * posizione COMPOSTA, per cui il server non mandava la sua posizione di
       * partenza: la ricostruzione partiva dall'apertura standard e questo
       * ramo scattava su ogni ply tranne lo zero, che mostrava una posizione
       * in cui la partita non era mai stata. Ora la board scende dal filo
       * (`setOnlineStartBoard`). La rete resta perche' la disallineatura fra
       * `startBoard` e `moveHistory` puo' nascere anche da un `game:update`
       * arrivato a meta' ricostruzione.
       */
      let board = state.startBoard ?? createInitialBoard(state.variant, state.ruleMode);
      try {
        for (let i = 0; i < index; i++) {
          board = applyMove(board, state.moveHistory[i]!);
        }
      } catch (err) {
        log.error('Game', 'reviewToIndex: ricostruzione impossibile, resto sul vivo', {
          index,
          totalMoves,
          hasStartBoard: state.startBoard !== null,
          error: err instanceof Error ? err.message : String(err),
        });
        return state.reviewIndex === null
          ? {}
          : { reviewIndex: null, reviewPieces: null, reviewKingMoveStreak: null, reviewTurn: null, selectedSquare: null, legalMoves: [] };
      }

      log.info('Game', 'reviewToIndex set (reconstructed)', { index, pieces: board.pieces.length });
      return {
        reviewIndex: index,
        reviewPieces: board.pieces,
        reviewTurn: board.currentTurn, // authoritative side to move — no White-first parity guess
        // `board` e' la posizione gia' rigiocata con `applyMove`: la striscia e' quella del
        // ply rivisto. Serve ai pallini della dama bloccata (vedi il campo nell'interfaccia).
        reviewKingMoveStreak: board.kingMoveStreak ?? null,
        selectedSquare: null,
        legalMoves: [],
      };
    });
  },

  reviewBack: () => {
    const state = useGameStore.getState();
    // Chevron entry point: only in AI modes, only when it's user's turn, only during play
    // 'pvp' qui e' una partita online/arena/torneo: nessuno riscrive la storia da solo.
    // In presenza invece si torna indietro liberamente, come sulla scacchiera d'analisi.
    if (state.gameMode === 'pvp') { log.info('Game', 'reviewBack blocked', { reason: 'pvp' }); return; }
    // In presenza non esiste "il mio turno": muovono entrambi da questo dispositivo.
    if (!isHotseatMode(state.gameMode) && state.currentTurn !== state.localPlayerColor) { log.info('Game', 'reviewBack blocked', { reason: 'not-user-turn' }); return; }
    if (state.status !== GameStatus.PLAYING) { log.info('Game', 'reviewBack blocked', { reason: 'not-playing' }); return; }
    if (state.captureInProgress) { log.info('Game', 'reviewBack blocked', { reason: 'mid-capture' }); return; }

    // Il chevron non scende sotto l'apertura sorteggiata: quelle semi-mosse sono
    // imposte, ritirarle non è un'opzione. Dalla lista mosse si possono comunque
    // guardare (la review è in sola lettura).
    const currentIdx = state.reviewIndex ?? state.moveHistory.length;
    if (currentIdx <= state.openingPlyCount) return;
    state.reviewToIndex(currentIdx - 1);
  },

  reviewForward: () => {
    const state = useGameStore.getState();
    if (state.reviewIndex === null) { log.info('Game', 'reviewForward blocked (live)', {}); return; }
    state.reviewToIndex(state.reviewIndex + 1);
  },

  reviewExit: () => {
    log.info('Game', 'reviewExit', {});
    set({ reviewIndex: null, reviewPieces: null, reviewKingMoveStreak: null, reviewTurn: null });
  },

  /**
   * Il rovescio esatto di `commitTakeback`: invece di tagliare la cronologia la
   * RIMETTE, rigiocando una linea salvata da `startBoard`.
   *
   * Si chiama subito dopo `startLocalGameFromBoard`, che ha appena azzerato tutto il
   * resto — selezione, trascinamento, orologio, revisione: per questo qui si scrivono
   * solo i campi che il replay cambia davvero.
   *
   * `moveHistory` e `positionHistory` vengono dalla board rigiocata e non ricostruiti a
   * mano: `applyMove` li mantiene entrambi, e `startBoard` parte da liste vuote, quindi
   * escono già nella convenzione dello store. Lo `status`, invece, si ridetermina con
   * `determineGameOutcome`: `applyMove` lascia solo lo stato, e qui serve anche il
   * MOTIVO, che è ciò che la schermata mostra.
   *
   * Una linea che non si rigioca fino in fondo (posizione cambiata sotto, dato vecchio)
   * si ferma dov'è arrivata: `replayLine` tronca invece di lanciare, e mezza linea
   * ricaricata è meglio di una damiera che non si apre.
   */
  replayLocalLine: (steps) => {
    if (steps.length === 0) return;
    set((state) => {
      const start = state.startBoard ?? createInitialBoard(state.variant, state.ruleMode);
      const { board, moves } = replayLine(start, steps);
      if (moves.length === 0) return {};
      log.info('Game', 'replayLocalLine', { chiesti: steps.length, rigiocati: moves.length });

      const { status, reason } = determineGameOutcome(board);
      return {
        pieces: board.pieces,
        currentTurn: board.currentTurn,
        status,
        endReason: reason,
        halfMoveClock: board.halfMoveClock,
        positionHistory: board.positionHistory ?? [],
        moveHistory: board.moveHistory,
        kingMoveStreak: board.kingMoveStreak,
        frisianEndgamePly: board.frisianEndgamePly ?? null,
      };
    });
  },

  // AI only: drop history after reviewIndex and resume play from that position.
  commitTakeback: (force = false) => {
    cancelCaptureReplayNow(); // si torna indietro: il fantasma mostrerebbe una mossa ritirata
    set((state) => {
      if (state.reviewIndex === null) return {};
      // pvp is hot-seat: in a real game nobody may rewrite history unilaterally. The
      // editor's analysis board is the exception — see the interface comment.
      if (state.gameMode === 'pvp' && !force) return {};
      // (l'hot-seat non passa di qui: `gameMode` e' 'hotseat', non 'pvp')

      // Cancel any AI work still in flight for the position we're leaving: clear a
      // pending think-dispatch and bump the generation so an already-dispatched
      // worker result is ignored on arrival (mirrors resetGame; without this a
      // stale AI move could land on the taken-back board and scramble pieces).
      if (state.aiTimeoutId) clearTimeout(state.aiTimeoutId);
      aiGeneration++;
      log.info('Game', 'commitTakeback (destructive truncation)', { fromIndex: state.reviewIndex, aiGeneration });

      // Non si torna indietro oltre l'apertura sorteggiata: se si stava guardando una
      // sua semi-mossa, il gioco riprende dal primo punto ritirabile invece di lasciare
      // un click morto.
      const idx = Math.max(state.reviewIndex, state.openingPlyCount);
      // Reconstruct the board at the reviewed position (reuse applyMove's BoardState),
      // starting from wherever this game began — not necessarily the standard opening.
      let board = state.startBoard ?? createInitialBoard(state.variant, state.ruleMode);
      for (let i = 0; i < idx; i++) {
        board = applyMove(board, state.moveHistory[i]!);
      }

      return {
        pieces: board.pieces,
        currentTurn: board.currentTurn,
        halfMoveClock: board.halfMoveClock,
        positionHistory: (state.positionHistory ?? []).slice(0, idx),
        moveHistory: state.moveHistory.slice(0, idx),
        // Core applyMove replay above already rebuilt the Frisian state — keep it on the live board.
        kingMoveStreak: board.kingMoveStreak,
        frisianEndgamePly: board.frisianEndgamePly,
        status: GameStatus.PLAYING, // reopen the game if it had ended
        endReason: null, // ...e con essa sparisce il motivo per cui era finita
        reviewIndex: null,
        reviewPieces: null,
        reviewKingMoveStreak: null,
        reviewTurn: null,
        selectedSquare: null,
        legalMoves: [],
        captureInProgress: null,
        premove: null,
        captureSourceChoices: [],
        pendingCaptureTarget: null,
        movableHint: null,
        isAIThinking: false,
        aiTimeoutId: null,
        drawOffered: false,
        aiDeclinedDraw: false,
      };
    });
  },

  // Clock actions
  setTimeControl: (preset: TimeControlPreset) => {
    aiGeneration++; // invalidate any AI result still in flight
    cancelCaptureReplayNow();
    log.info('Game', 'setTimeControl', { preset, aiGeneration });
    set((state) => {
      const newClockState = createClockState(preset);
      // Come setGameMode/setVariant: si riparte da una partita standard.
      const newBoard = createInitialBoard(state.variant, DEFAULT_GAME_MODE);
      return {
        pieces: newBoard.pieces,
        currentTurn: newBoard.currentTurn,
        status: GameStatus.PLAYING,
        moveHistory: [],
        halfMoveClock: 0,
        positionHistory: [],
        kingMoveStreak: undefined,
        frisianEndgamePly: null,
        startBoard: null, // back to the standard opening
        ruleMode: DEFAULT_GAME_MODE,
        openingMode: DEFAULT_OPENING_MODE,
        openingPlyCount: 0,
        drawnOpening: null,
        endReason: null,
        selectedSquare: null,
        legalMoves: [],
        captureInProgress: null,
        premove: null,
        captureSourceChoices: [],
        pendingCaptureTarget: null,
        movableHint: null,
        clockState: newClockState,
        timeControlPreset: preset,
        isAIThinking: false,
        drawOffered: false,
        aiDeclinedDraw: false,
        drawTimeoutId: null,
      };
    });
  },

  updateClock: (player: Color, timeRemaining: number) => {
    set((state) => ({
      clockState: {
        ...state.clockState,
        [player === Color.WHITE ? 'white' : 'black']: {
          ...state.clockState[player === Color.WHITE ? 'white' : 'black'],
          timeRemaining,
        },
      },
    }));
  },

  handleTimeExpired: (player: Color) => {
    set((state) => {
      // If no time control or game already ended, ignore
      if (state.timeControlPreset === 'none' || state.status !== GameStatus.PLAYING) {
        return {};
      }

      // Player who ran out of time loses
      const winner = player === Color.WHITE ? Color.BLACK : Color.WHITE;
      return {
        status: winner === Color.WHITE ? GameStatus.WHITE_WINS : GameStatus.BLACK_WINS,
        endReason: { kind: 'timeout', side: player } as LocalEndAction,
        clockState: {
          ...state.clockState,
          [player === Color.WHITE ? 'white' : 'black']: {
            timeRemaining: 0,
            isRunning: false,
          },
        },
      };
    });
  },

  addIncrement: (player: Color) => {
    set((state) => {
      const increment = state.clockState.timeControl.increment;
      if (increment === 0) return {};

      const key = player === Color.WHITE ? 'white' : 'black';
      return {
        clockState: {
          ...state.clockState,
          [key]: {
            ...state.clockState[key],
            timeRemaining: state.clockState[key].timeRemaining + increment,
          },
        },
      };
    });
  },
}));
